import { useState, useEffect, useRef } from "react";
import { Routes, Route, Link, useLocation, useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { 
  LayoutDashboard, Users, Car, Settings, LogOut, Menu, X, 
  TrendingUp, FileText, ChevronDown, Bell, Search, Download, Copy, Share2, Check, Trash2,
  Eye, MousePointer, Monitor, Smartphone, Globe, CalendarIcon
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import html2canvas from "html2canvas";
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip, LineChart, Line, XAxis, YAxis, CartesianGrid, BarChart, Bar } from "recharts";
import { jsPDF } from "jspdf";
import { API, useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Progress } from "../components/ui/progress";
import { Badge } from "../components/ui/badge";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";

const LOGO_URL = "/logo.png";

// Pie chart colors
const PIE_COLORS = ['#E91E8C', '#FF6B9D', '#C4167A', '#FFB6D1', '#8B1254', '#FF85B3', '#A01568', '#FFD4E5'];

// Download helper functions
const downloadCSV = (data, filename, headers) => {
  const csvContent = [
    headers.join(','),
    ...data.map(row => headers.map(h => row[h.toLowerCase().replace(/ /g, '_')] ?? row[h] ?? '').join(','))
  ].join('\n');
  
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = `${filename}_${new Date().toISOString().split('T')[0]}.csv`;
  link.click();
};

const downloadChartAsImage = async (elementId, filename) => {
  const element = document.getElementById(elementId);
  if (!element) {
    toast.error('Chart not found');
    return;
  }
  
  try {
    const canvas = await html2canvas(element, {
      backgroundColor: '#ffffff',
      scale: 2
    });
    const link = document.createElement('a');
    link.download = `${filename}_${new Date().toISOString().split('T')[0]}.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();
    toast.success('Chart downloaded!');
  } catch (error) {
    toast.error('Failed to download chart');
  }
};

const downloadFullReport = async (trafficData, analytics, period) => {
  toast.info('Generating report with charts...');
  
  // Create a hidden container for the full report
  const reportContainer = document.createElement('div');
  reportContainer.style.position = 'absolute';
  reportContainer.style.left = '-9999px';
  reportContainer.style.backgroundColor = '#ffffff';
  reportContainer.style.padding = '40px';
  reportContainer.style.width = '1200px';
  reportContainer.style.fontFamily = 'Arial, sans-serif';
  
  const reportDate = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  // period is now passed as the label directly
  const periodLabel = period;
  
  // Build HTML report
  let html = `
    <div style="text-align: center; margin-bottom: 30px;">
      <h1 style="color: #E91E8C; margin-bottom: 10px;">Trish Drives Honda</h1>
      <h2 style="color: #2C2420; margin-bottom: 5px;">Analytics Report</h2>
      <p style="color: #6B5E55;">Generated: ${reportDate} | Period: ${periodLabel}</p>
    </div>
    
    <div style="display: grid; grid-template-columns: repeat(4, 1fr); gap: 20px; margin-bottom: 30px;">
      <div style="background: #f0f9ff; padding: 20px; border-radius: 10px; text-align: center;">
        <p style="font-size: 32px; font-weight: bold; color: #2563eb; margin: 0;">${trafficData?.today_views || 0}</p>
        <p style="color: #6B5E55; margin: 5px 0 0 0;">Today's Views</p>
      </div>
      <div style="background: #f0fdf4; padding: 20px; border-radius: 10px; text-align: center;">
        <p style="font-size: 32px; font-weight: bold; color: #16a34a; margin: 0;">${trafficData?.today_visitors || 0}</p>
        <p style="color: #6B5E55; margin: 5px 0 0 0;">Today's Visitors</p>
      </div>
      <div style="background: #faf5ff; padding: 20px; border-radius: 10px; text-align: center;">
        <p style="font-size: 32px; font-weight: bold; color: #9333ea; margin: 0;">${trafficData?.total_views || 0}</p>
        <p style="color: #6B5E55; margin: 5px 0 0 0;">Total Views</p>
      </div>
      <div style="background: #fdf2f8; padding: 20px; border-radius: 10px; text-align: center;">
        <p style="font-size: 32px; font-weight: bold; color: #E91E8C; margin: 0;">${trafficData?.unique_visitors || 0}</p>
        <p style="color: #6B5E55; margin: 5px 0 0 0;">Unique Visitors</p>
      </div>
    </div>
    
    <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 20px; margin-bottom: 30px;">
      <div style="background: #fff5f9; padding: 20px; border-radius: 10px; text-align: center;">
        <p style="font-size: 32px; font-weight: bold; color: #E91E8C; margin: 0;">${analytics?.total_leads || 0}</p>
        <p style="color: #6B5E55; margin: 5px 0 0 0;">Total Leads</p>
      </div>
      <div style="background: #f0fdf4; padding: 20px; border-radius: 10px; text-align: center;">
        <p style="font-size: 32px; font-weight: bold; color: #16a34a; margin: 0;">${analytics?.closed_deals || 0}</p>
        <p style="color: #6B5E55; margin: 5px 0 0 0;">Closed Deals</p>
      </div>
    </div>
  `;
  
  // Page Views by Day Table
  if (trafficData?.views_by_day?.length > 0) {
    html += `
      <div style="margin-bottom: 30px;">
        <h3 style="color: #2C2420; border-bottom: 2px solid #E91E8C; padding-bottom: 10px;">Page Views by Day</h3>
        <table style="width: 100%; border-collapse: collapse;">
          <tr style="background: #f9f7f2;">
            <th style="padding: 10px; text-align: left; border-bottom: 1px solid #e5e7eb;">Date</th>
            <th style="padding: 10px; text-align: right; border-bottom: 1px solid #e5e7eb;">Views</th>
          </tr>
          ${trafficData.views_by_day.map(d => `
            <tr>
              <td style="padding: 10px; border-bottom: 1px solid #e5e7eb;">${new Date(d.date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</td>
              <td style="padding: 10px; text-align: right; border-bottom: 1px solid #e5e7eb; font-weight: bold; color: #E91E8C;">${d.views}</td>
            </tr>
          `).join('')}
        </table>
      </div>
    `;
  }
  
  // Lead Submissions by Day Table
  if (trafficData?.leads_by_day?.length > 0) {
    html += `
      <div style="margin-bottom: 30px;">
        <h3 style="color: #2C2420; border-bottom: 2px solid #E91E8C; padding-bottom: 10px;">Lead Submissions by Day</h3>
        <table style="width: 100%; border-collapse: collapse;">
          <tr style="background: #f9f7f2;">
            <th style="padding: 10px; text-align: left; border-bottom: 1px solid #e5e7eb;">Date</th>
            <th style="padding: 10px; text-align: right; border-bottom: 1px solid #e5e7eb;">Leads</th>
          </tr>
          ${trafficData.leads_by_day.map(d => `
            <tr>
              <td style="padding: 10px; border-bottom: 1px solid #e5e7eb;">${new Date(d.date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</td>
              <td style="padding: 10px; text-align: right; border-bottom: 1px solid #e5e7eb; font-weight: bold; color: #E91E8C;">${d.leads}</td>
            </tr>
          `).join('')}
        </table>
      </div>
    `;
  }
  
  // Traffic Sources & Devices side by side
  html += `<div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 20px; margin-bottom: 30px;">`;
  
  if (trafficData?.traffic_sources) {
    // Filter out development/preview environment sources
    const filteredSources = Object.entries(trafficData.traffic_sources)
      .filter(([source]) => !['app.emergent.sh', '', null].includes(source?.toLowerCase()));
    const totalSources = filteredSources.reduce((sum, [, count]) => sum + count, 0);
    if (filteredSources.length > 0) {
      html += `
        <div>
          <h3 style="color: #2C2420; border-bottom: 2px solid #E91E8C; padding-bottom: 10px;">Traffic Sources</h3>
          <table style="width: 100%; border-collapse: collapse;">
            ${filteredSources.map(([source, count]) => `
              <tr>
                <td style="padding: 8px 10px; border-bottom: 1px solid #e5e7eb;">${source}</td>
                <td style="padding: 8px 10px; text-align: right; border-bottom: 1px solid #e5e7eb;">${count} (${Math.round(count/totalSources*100)}%)</td>
              </tr>
            `).join('')}
          </table>
        </div>
      `;
    }
  }
  
  if (trafficData?.devices) {
    const totalDevices = Object.values(trafficData.devices).reduce((a, b) => a + b, 0);
    html += `
      <div>
        <h3 style="color: #2C2420; border-bottom: 2px solid #E91E8C; padding-bottom: 10px;">Device Breakdown</h3>
        <table style="width: 100%; border-collapse: collapse;">
          ${Object.entries(trafficData.devices).map(([device, count]) => `
            <tr>
              <td style="padding: 8px 10px; border-bottom: 1px solid #e5e7eb;">${device}</td>
              <td style="padding: 8px 10px; text-align: right; border-bottom: 1px solid #e5e7eb;">${count} (${Math.round(count/totalDevices*100)}%)</td>
            </tr>
          `).join('')}
        </table>
      </div>
    `;
  }
  html += `</div>`;
  
  // Top Pages
  if (trafficData?.top_pages?.length > 0) {
    html += `
      <div style="margin-bottom: 30px;">
        <h3 style="color: #2C2420; border-bottom: 2px solid #E91E8C; padding-bottom: 10px;">Top Pages</h3>
        <table style="width: 100%; border-collapse: collapse;">
          <tr style="background: #f9f7f2;">
            <th style="padding: 10px; text-align: left; border-bottom: 1px solid #e5e7eb;">#</th>
            <th style="padding: 10px; text-align: left; border-bottom: 1px solid #e5e7eb;">Page</th>
            <th style="padding: 10px; text-align: right; border-bottom: 1px solid #e5e7eb;">Views</th>
          </tr>
          ${trafficData.top_pages.map((p, i) => `
            <tr>
              <td style="padding: 10px; border-bottom: 1px solid #e5e7eb; color: #E91E8C; font-weight: bold;">${i + 1}</td>
              <td style="padding: 10px; border-bottom: 1px solid #e5e7eb;">${p.page === '/' ? 'Home' : p.page}</td>
              <td style="padding: 10px; text-align: right; border-bottom: 1px solid #e5e7eb;">${p.views}</td>
            </tr>
          `).join('')}
        </table>
      </div>
    `;
  }
  
  // Top Countries & Cities side by side
  html += `<div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 20px; margin-bottom: 30px;">`;
  
  if (trafficData?.top_countries?.length > 0) {
    html += `
      <div>
        <h3 style="color: #2C2420; border-bottom: 2px solid #E91E8C; padding-bottom: 10px;">Top Countries</h3>
        <table style="width: 100%; border-collapse: collapse;">
          ${trafficData.top_countries.map((c, i) => `
            <tr>
              <td style="padding: 8px 10px; border-bottom: 1px solid #e5e7eb;"><span style="color: #E91E8C; font-weight: bold;">${i + 1}.</span> ${c.country}</td>
              <td style="padding: 8px 10px; text-align: right; border-bottom: 1px solid #e5e7eb;">${c.count} visits</td>
            </tr>
          `).join('')}
        </table>
      </div>
    `;
  }
  
  if (trafficData?.top_cities?.length > 0) {
    html += `
      <div>
        <h3 style="color: #2C2420; border-bottom: 2px solid #E91E8C; padding-bottom: 10px;">Top Cities</h3>
        <table style="width: 100%; border-collapse: collapse;">
          ${trafficData.top_cities.map((c, i) => `
            <tr>
              <td style="padding: 8px 10px; border-bottom: 1px solid #e5e7eb;"><span style="color: #E91E8C; font-weight: bold;">${i + 1}.</span> ${c.city}, ${c.country}</td>
              <td style="padding: 8px 10px; text-align: right; border-bottom: 1px solid #e5e7eb;">${c.count} visits</td>
            </tr>
          `).join('')}
        </table>
      </div>
    `;
  }
  html += `</div>`;
  
  // Leads by Status & Source
  html += `<div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 20px; margin-bottom: 30px;">`;
  
  if (analytics?.leads_by_status) {
    html += `
      <div>
        <h3 style="color: #2C2420; border-bottom: 2px solid #E91E8C; padding-bottom: 10px;">Leads by Status</h3>
        <table style="width: 100%; border-collapse: collapse;">
          ${Object.entries(analytics.leads_by_status).map(([status, count]) => `
            <tr>
              <td style="padding: 8px 10px; border-bottom: 1px solid #e5e7eb;">${status}</td>
              <td style="padding: 8px 10px; text-align: right; border-bottom: 1px solid #e5e7eb; font-weight: bold;">${count}</td>
            </tr>
          `).join('')}
        </table>
      </div>
    `;
  }
  
  if (analytics?.leads_by_source) {
    html += `
      <div>
        <h3 style="color: #2C2420; border-bottom: 2px solid #E91E8C; padding-bottom: 10px;">Leads by Source</h3>
        <table style="width: 100%; border-collapse: collapse;">
          ${Object.entries(analytics.leads_by_source).map(([source, count]) => `
            <tr>
              <td style="padding: 8px 10px; border-bottom: 1px solid #e5e7eb;">${source}</td>
              <td style="padding: 8px 10px; text-align: right; border-bottom: 1px solid #e5e7eb; font-weight: bold;">${count}</td>
            </tr>
          `).join('')}
        </table>
      </div>
    `;
  }
  html += `</div>`;
  
  // Footer
  html += `
    <div style="text-align: center; margin-top: 40px; padding-top: 20px; border-top: 1px solid #e5e7eb; color: #6B5E55;">
      <p>Generated by Trish Drives Honda CRM</p>
    </div>
  `;
  
  reportContainer.innerHTML = html;
  document.body.appendChild(reportContainer);
  
  try {
    const canvas = await html2canvas(reportContainer, {
      backgroundColor: '#ffffff',
      scale: 2,
      useCORS: true
    });
    
    const link = document.createElement('a');
    link.download = `trish_honda_analytics_report_${new Date().toISOString().split('T')[0]}.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();
    toast.success('Report downloaded!');
  } catch (error) {
    console.error('Error generating report:', error);
    toast.error('Failed to generate report');
  } finally {
    document.body.removeChild(reportContainer);
  }
};

const downloadAnalyticsReport = (trafficData, analytics, period) => {
  const reportDate = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  // period is now passed as the label directly
  const periodLabel = period;
  
  let report = `TRISH DRIVES HONDA - ANALYTICS REPORT\n`;
  report += `Generated: ${reportDate}\n`;
  report += `Period: ${periodLabel}\n`;
  report += `${'='.repeat(50)}\n\n`;
  
  // Traffic Summary
  report += `TRAFFIC SUMMARY\n`;
  report += `-`.repeat(30) + `\n`;
  report += `Today's Views: ${trafficData?.today_views || 0}\n`;
  report += `Today's Visitors: ${trafficData?.today_visitors || 0}\n`;
  report += `Total Views: ${trafficData?.total_views || 0}\n`;
  report += `Unique Visitors: ${trafficData?.unique_visitors || 0}\n\n`;
  
  // Lead Summary
  report += `LEAD SUMMARY\n`;
  report += `-`.repeat(30) + `\n`;
  report += `Total Leads: ${analytics?.total_leads || 0}\n`;
  report += `Closed Deals: ${analytics?.closed_deals || 0}\n\n`;
  
  // Page Views by Day
  if (trafficData?.views_by_day?.length > 0) {
    report += `PAGE VIEWS BY DAY\n`;
    report += `-`.repeat(30) + `\n`;
    report += `Date,Views\n`;
    trafficData.views_by_day.forEach(d => {
      report += `${d.date},${d.views}\n`;
    });
    report += `\n`;
  }
  
  // Leads by Day
  if (trafficData?.leads_by_day?.length > 0) {
    report += `LEAD SUBMISSIONS BY DAY\n`;
    report += `-`.repeat(30) + `\n`;
    report += `Date,Leads\n`;
    trafficData.leads_by_day.forEach(d => {
      report += `${d.date},${d.leads}\n`;
    });
    report += `\n`;
  }
  
  // Traffic Sources
  if (trafficData?.traffic_sources) {
    // Filter out development/preview environment sources
    const filteredSources = Object.entries(trafficData.traffic_sources)
      .filter(([source]) => !['app.emergent.sh', '', null].includes(source?.toLowerCase()));
    if (filteredSources.length > 0) {
      report += `TRAFFIC SOURCES\n`;
      report += `-`.repeat(30) + `\n`;
      report += `Source,Count\n`;
      filteredSources.forEach(([source, count]) => {
        report += `${source},${count}\n`;
      });
      report += `\n`;
    }
  }
  
  // Device Breakdown
  if (trafficData?.devices) {
    report += `DEVICE BREAKDOWN\n`;
    report += `-`.repeat(30) + `\n`;
    report += `Device,Count\n`;
    Object.entries(trafficData.devices).forEach(([device, count]) => {
      report += `${device},${count}\n`;
    });
    report += `\n`;
  }
  
  // Top Pages
  if (trafficData?.top_pages?.length > 0) {
    report += `TOP PAGES\n`;
    report += `-`.repeat(30) + `\n`;
    report += `Page,Views\n`;
    trafficData.top_pages.forEach(p => {
      report += `${p.page},${p.views}\n`;
    });
    report += `\n`;
  }
  
  // Top Countries
  if (trafficData?.top_countries?.length > 0) {
    report += `TOP COUNTRIES\n`;
    report += `-`.repeat(30) + `\n`;
    report += `Country,Visits\n`;
    trafficData.top_countries.forEach(c => {
      report += `${c.country},${c.count}\n`;
    });
    report += `\n`;
  }
  
  // Top Cities
  if (trafficData?.top_cities?.length > 0) {
    report += `TOP CITIES\n`;
    report += `-`.repeat(30) + `\n`;
    report += `City,Country,Visits\n`;
    trafficData.top_cities.forEach(c => {
      report += `${c.city},${c.country},${c.count}\n`;
    });
    report += `\n`;
  }
  
  // Leads by Status
  if (analytics?.leads_by_status) {
    report += `LEADS BY STATUS\n`;
    report += `-`.repeat(30) + `\n`;
    report += `Status,Count\n`;
    Object.entries(analytics.leads_by_status).forEach(([status, count]) => {
      report += `${status},${count}\n`;
    });
    report += `\n`;
  }
  
  // Leads by Source
  if (analytics?.leads_by_source) {
    report += `LEADS BY SOURCE\n`;
    report += `-`.repeat(30) + `\n`;
    report += `Source,Count\n`;
    Object.entries(analytics.leads_by_source).forEach(([source, count]) => {
      report += `${source},${count}\n`;
    });
  }
  
  const blob = new Blob([report], { type: 'text/plain;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = `analytics_report_${new Date().toISOString().split('T')[0]}.txt`;
  link.click();
};

const downloadPDFReport = async (trafficData, analytics, period) => {
  toast.info('Generating comprehensive PDF report...');
  
  const pdf = new jsPDF('p', 'mm', 'a4');
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 15;
  let yPos = margin;
  
  const reportDate = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  // period is now passed as the label directly
  const periodLabel = period;
  const prevPeriodLabel = 'previous period';
  
  // Helper functions
  const addTitle = (text, size = 16) => {
    pdf.setFontSize(size);
    pdf.setTextColor(233, 30, 140);
    pdf.setFont(undefined, 'bold');
    pdf.text(text, margin, yPos);
    yPos += size * 0.5;
  };
  
  const addSubtitle = (text, size = 12) => {
    pdf.setFontSize(size);
    pdf.setTextColor(44, 36, 32);
    pdf.setFont(undefined, 'bold');
    pdf.text(text, margin, yPos);
    yPos += size * 0.4;
  };
  
  const addText = (text, size = 10, color = [107, 94, 85]) => {
    pdf.setFontSize(size);
    pdf.setTextColor(...color);
    pdf.setFont(undefined, 'normal');
    pdf.text(text, margin, yPos);
    yPos += size * 0.5;
  };
  
  const addLine = () => {
    pdf.setDrawColor(229, 231, 235);
    pdf.line(margin, yPos, pageWidth - margin, yPos);
    yPos += 3;
  };
  
  const checkNewPage = (neededSpace = 30) => {
    if (yPos + neededSpace > pageHeight - margin) {
      pdf.addPage();
      yPos = margin;
    }
  };
  
  const formatChange = (change) => {
    if (change > 0) return `↑ ${change}%`;
    if (change < 0) return `↓ ${Math.abs(change)}%`;
    return '0%';
  };
  
  const getChangeColor = (change) => {
    if (change > 0) return [22, 163, 74]; // Green
    if (change < 0) return [220, 38, 38]; // Red
    return [107, 94, 85]; // Gray
  };
  
  const addTable = (headers, data, colWidths) => {
    const tableWidth = colWidths.reduce((a, b) => a + b, 0);
    const startX = margin;
    const rowHeight = 8;
    
    // Header
    pdf.setFillColor(249, 247, 242);
    pdf.rect(startX, yPos - 5, tableWidth, rowHeight, 'F');
    pdf.setFontSize(9);
    pdf.setTextColor(44, 36, 32);
    pdf.setFont(undefined, 'bold');
    
    let xPos = startX;
    headers.forEach((header, i) => {
      pdf.text(header, xPos + 2, yPos);
      xPos += colWidths[i];
    });
    yPos += rowHeight;
    
    // Data rows
    pdf.setFont(undefined, 'normal');
    pdf.setTextColor(107, 94, 85);
    
    data.forEach(row => {
      checkNewPage(rowHeight);
      xPos = startX;
      row.forEach((cell, i) => {
        pdf.text(String(cell).substring(0, 35), xPos + 2, yPos);
        xPos += colWidths[i];
      });
      yPos += rowHeight - 2;
    });
    yPos += 5;
  };
  
  // === PDF CONTENT ===
  
  // Header
  addTitle('TRISH DRIVES HONDA', 22);
  yPos += 2;
  pdf.setFontSize(14);
  pdf.setTextColor(107, 94, 85);
  pdf.setFont(undefined, 'normal');
  pdf.text('Analytics Report', margin, yPos);
  yPos += 8;
  addText(`Generated: ${reportDate}  |  Period: ${periodLabel}`);
  yPos += 3;
  addLine();
  yPos += 5;
  
  // === EXECUTIVE SUMMARY ===
  checkNewPage(60);
  addSubtitle('EXECUTIVE SUMMARY', 14);
  yPos += 5;
  
  pdf.setFillColor(255, 245, 249);
  pdf.roundedRect(margin, yPos, pageWidth - margin * 2, 45, 3, 3, 'F');
  yPos += 8;
  
  const conversionRate = trafficData?.conversion_rate || 0;
  const totalLeads = trafficData?.current_leads || analytics?.total_leads || 0;
  const closedDeals = analytics?.closed_deals || 0;
  const totalSales = trafficData?.total_sales_value || 0;
  const uniqueVisitors = trafficData?.unique_visitors || 0;
  
  pdf.setFontSize(10);
  pdf.setTextColor(44, 36, 32);
  pdf.setFont(undefined, 'normal');
  
  const summaryLines = [
    `• Your website received ${trafficData?.total_views || 0} page views from ${uniqueVisitors} unique visitors during this period.`,
    `• ${totalLeads} new leads were generated with a conversion rate of ${conversionRate}%.`,
    `• ${closedDeals} deals were closed with a total sales value of US$${totalSales.toLocaleString()}.`,
    `• Top traffic source: ${Object.keys(trafficData?.traffic_sources || {})[0] || 'Direct'} | Top page: ${(trafficData?.top_pages?.[0]?.page === '/' ? 'Home' : trafficData?.top_pages?.[0]?.page) || 'Home'}`,
  ];
  
  summaryLines.forEach(line => {
    pdf.text(line, margin + 5, yPos);
    yPos += 6;
  });
  yPos += 10;
  
  // === KEY METRICS WITH COMPARISONS ===
  checkNewPage(50);
  addSubtitle('KEY METRICS', 14);
  yPos += 5;
  
  // Row 1: Traffic metrics with comparisons
  const metricsRow1 = [
    { label: "Total Views", value: trafficData?.total_views || 0, change: trafficData?.views_change || 0 },
    { label: "Unique Visitors", value: trafficData?.unique_visitors || 0, change: trafficData?.visitors_change || 0 },
    { label: "New Leads", value: totalLeads, change: trafficData?.leads_change || 0 },
    { label: "Conversion Rate", value: `${conversionRate}%`, change: null }
  ];
  
  const boxWidth = (pageWidth - margin * 2 - 15) / 4;
  metricsRow1.forEach((metric, i) => {
    const x = margin + (i * (boxWidth + 5));
    pdf.setFillColor(249, 250, 251);
    pdf.roundedRect(x, yPos, boxWidth, 28, 3, 3, 'F');
    
    // Value
    pdf.setFontSize(18);
    pdf.setTextColor(233, 30, 140);
    pdf.setFont(undefined, 'bold');
    pdf.text(String(metric.value), x + boxWidth/2, yPos + 12, { align: 'center' });
    
    // Label
    pdf.setFontSize(8);
    pdf.setTextColor(107, 94, 85);
    pdf.setFont(undefined, 'normal');
    pdf.text(metric.label, x + boxWidth/2, yPos + 18, { align: 'center' });
    
    // Change indicator
    if (metric.change !== null) {
      const changeColor = getChangeColor(metric.change);
      pdf.setFontSize(8);
      pdf.setTextColor(...changeColor);
      pdf.text(`${formatChange(metric.change)} vs ${prevPeriodLabel}`, x + boxWidth/2, yPos + 24, { align: 'center' });
    }
  });
  yPos += 35;
  
  // Row 2: Sales metrics
  const metricsRow2 = [
    { label: "Closed Deals", value: closedDeals, color: [22, 163, 74] },
    { label: "Total Sales Value", value: `US$${totalSales.toLocaleString()}`, color: [22, 163, 74] },
    { label: "Today's Views", value: trafficData?.today_views || 0, color: [37, 99, 235] },
    { label: "Today's Visitors", value: trafficData?.today_visitors || 0, color: [37, 99, 235] }
  ];
  
  metricsRow2.forEach((metric, i) => {
    const x = margin + (i * (boxWidth + 5));
    pdf.setFillColor(240, 253, 244);
    if (i >= 2) pdf.setFillColor(239, 246, 255);
    pdf.roundedRect(x, yPos, boxWidth, 22, 3, 3, 'F');
    
    pdf.setFontSize(14);
    pdf.setTextColor(...metric.color);
    pdf.setFont(undefined, 'bold');
    pdf.text(String(metric.value), x + boxWidth/2, yPos + 10, { align: 'center' });
    
    pdf.setFontSize(8);
    pdf.setTextColor(107, 94, 85);
    pdf.setFont(undefined, 'normal');
    pdf.text(metric.label, x + boxWidth/2, yPos + 17, { align: 'center' });
  });
  yPos += 30;
  
  // === BOOKING TYPE BREAKDOWN ===
  if (trafficData?.booking_types && Object.keys(trafficData.booking_types).length > 0) {
    checkNewPage(50);
    addSubtitle('BOOKING TYPE BREAKDOWN', 12);
    yPos += 5;
    
    const totalBookings = Object.values(trafficData.booking_types).reduce((a, b) => a + b, 0);
    const bookingData = Object.entries(trafficData.booking_types).map(([type, count]) => [
      type,
      count,
      `${Math.round(count/totalBookings*100)}%`
    ]);
    addTable(['Booking Type', 'Count', 'Percentage'], bookingData, [90, 40, 40]);
  }
  
  // === PAGE VIEWS BY DAY ===
  if (trafficData?.views_by_day?.length > 0) {
    checkNewPage(60);
    addSubtitle('PAGE VIEWS BY DAY', 12);
    yPos += 5;
    const viewsData = trafficData.views_by_day.map(d => [
      new Date(d.date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }),
      d.views
    ]);
    addTable(['Date', 'Views'], viewsData, [100, 50]);
  }
  
  // === LEAD SUBMISSIONS BY DAY ===
  if (trafficData?.leads_by_day?.length > 0) {
    checkNewPage(60);
    addSubtitle('LEAD SUBMISSIONS BY DAY', 12);
    yPos += 5;
    const leadsData = trafficData.leads_by_day.map(d => [
      new Date(d.date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }),
      d.leads
    ]);
    addTable(['Date', 'Leads'], leadsData, [100, 50]);
  }
  
  // === TRAFFIC SOURCES ===
  if (trafficData?.traffic_sources && Object.keys(trafficData.traffic_sources).length > 0) {
    // Filter out development/preview environment sources
    const filteredSources = Object.entries(trafficData.traffic_sources)
      .filter(([source]) => !['app.emergent.sh', '', null].includes(source?.toLowerCase()));
    if (filteredSources.length > 0) {
      checkNewPage(60);
      addSubtitle('TRAFFIC SOURCES', 12);
      yPos += 5;
      const totalSources = filteredSources.reduce((sum, [, count]) => sum + count, 0);
      const sourcesData = filteredSources.map(([source, count]) => [
        source,
        count,
        `${Math.round(count/totalSources*100)}%`
      ]);
      addTable(['Source', 'Count', 'Percentage'], sourcesData, [90, 40, 40]);
    }
  }
  
  // === DEVICE BREAKDOWN ===
  if (trafficData?.devices && Object.keys(trafficData.devices).length > 0) {
    checkNewPage(40);
    addSubtitle('DEVICE BREAKDOWN', 12);
    yPos += 5;
    const totalDevices = Object.values(trafficData.devices).reduce((a, b) => a + b, 0);
    const devicesData = Object.entries(trafficData.devices).map(([device, count]) => [
      device,
      count,
      `${Math.round(count/totalDevices*100)}%`
    ]);
    addTable(['Device', 'Count', 'Percentage'], devicesData, [90, 40, 40]);
  }
  
  // === TOP PAGES ===
  if (trafficData?.top_pages?.length > 0) {
    checkNewPage(70);
    addSubtitle('TOP PAGES', 12);
    yPos += 5;
    const pagesData = trafficData.top_pages.map((p, i) => [
      `${i + 1}.`,
      p.page === '/' ? 'Home' : p.page,
      p.views
    ]);
    addTable(['#', 'Page', 'Views'], pagesData, [15, 115, 35]);
  }
  
  // === TOP COUNTRIES ===
  if (trafficData?.top_countries?.length > 0) {
    checkNewPage(60);
    addSubtitle('TOP COUNTRIES', 12);
    yPos += 5;
    const countriesData = trafficData.top_countries.map((c, i) => [
      `${i + 1}.`,
      c.country,
      c.count
    ]);
    addTable(['#', 'Country', 'Visits'], countriesData, [15, 110, 40]);
  }
  
  // === TOP CITIES ===
  if (trafficData?.top_cities?.length > 0) {
    checkNewPage(60);
    addSubtitle('TOP CITIES', 12);
    yPos += 5;
    const citiesData = trafficData.top_cities.map((c, i) => [
      `${i + 1}.`,
      c.city,
      c.country,
      c.count
    ]);
    addTable(['#', 'City', 'Country', 'Visits'], citiesData, [15, 60, 60, 30]);
  }
  
  // === LEADS BY STATUS ===
  if (analytics?.leads_by_status && Object.keys(analytics.leads_by_status).length > 0) {
    checkNewPage(70);
    addSubtitle('LEADS BY STATUS', 12);
    yPos += 5;
    const statusData = Object.entries(analytics.leads_by_status).map(([status, count]) => [
      status,
      count
    ]);
    addTable(['Status', 'Count'], statusData, [130, 35]);
  }
  
  // === LEADS BY SOURCE ===
  if (analytics?.leads_by_source && Object.keys(analytics.leads_by_source).length > 0) {
    checkNewPage(60);
    addSubtitle('LEADS BY SOURCE', 12);
    yPos += 5;
    const sourceData = Object.entries(analytics.leads_by_source).map(([source, count]) => [
      source,
      count
    ]);
    addTable(['Source', 'Count'], sourceData, [130, 35]);
  }
  
  // === CAPTURE CHARTS AS IMAGES ===
  // Capture Page Views Chart
  const pageViewsChart = document.getElementById('page-views-chart');
  if (pageViewsChart) {
    try {
      checkNewPage(80);
      addSubtitle('PAGE VIEWS CHART', 12);
      yPos += 5;
      const canvas = await html2canvas(pageViewsChart, { backgroundColor: '#ffffff', scale: 2 });
      const imgData = canvas.toDataURL('image/png');
      const imgWidth = pageWidth - margin * 2;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      pdf.addImage(imgData, 'PNG', margin, yPos, imgWidth, Math.min(imgHeight, 60));
      yPos += Math.min(imgHeight, 60) + 10;
    } catch (e) {
      console.log('Could not capture page views chart');
    }
  }
  
  // Capture Traffic Sources Chart
  const trafficSourcesChart = document.getElementById('traffic-sources-chart');
  if (trafficSourcesChart) {
    try {
      checkNewPage(80);
      addSubtitle('TRAFFIC SOURCES CHART', 12);
      yPos += 5;
      const canvas = await html2canvas(trafficSourcesChart, { backgroundColor: '#ffffff', scale: 2 });
      const imgData = canvas.toDataURL('image/png');
      const imgWidth = (pageWidth - margin * 2) / 2;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      pdf.addImage(imgData, 'PNG', margin, yPos, imgWidth, Math.min(imgHeight, 60));
      yPos += Math.min(imgHeight, 60) + 10;
    } catch (e) {
      console.log('Could not capture traffic sources chart');
    }
  }
  
  // Capture Leads Chart
  const leadsChart = document.getElementById('leads-chart');
  if (leadsChart) {
    try {
      checkNewPage(80);
      addSubtitle('LEAD SUBMISSIONS CHART', 12);
      yPos += 5;
      const canvas = await html2canvas(leadsChart, { backgroundColor: '#ffffff', scale: 2 });
      const imgData = canvas.toDataURL('image/png');
      const imgWidth = pageWidth - margin * 2;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      pdf.addImage(imgData, 'PNG', margin, yPos, imgWidth, Math.min(imgHeight, 60));
      yPos += Math.min(imgHeight, 60) + 10;
    } catch (e) {
      console.log('Could not capture leads chart');
    }
  }
  
  // Footer on last page
  pdf.setFontSize(8);
  pdf.setTextColor(150, 150, 150);
  pdf.text('Generated by Trish Drives Honda CRM', pageWidth / 2, pageHeight - 10, { align: 'center' });
  
  // Save PDF
  pdf.save(`trish_honda_analytics_${new Date().toISOString().split('T')[0]}.pdf`);
  toast.success('Comprehensive PDF report downloaded!');
};

const LEAD_STATUSES = [
  "New / Not Contacted",
  "Lead Contacted",
  "Contacted but Undecided",
  "Appointment Booked",
  "Documents Submitted",
  "Approved",
  "Deal Closed / Delivered",
  "Cancelled / Loan Denied"
];

const STATUS_PROGRESS = {
  "New / Not Contacted": 0,
  "Lead Contacted": 15,
  "Contacted but Undecided": 25,
  "Appointment Booked": 40,
  "Documents Submitted": 60,
  "Approved": 80,
  "Deal Closed / Delivered": 100,
  "Cancelled / Loan Denied": 0
};

const STATUS_COLORS = {
  "New / Not Contacted": "bg-blue-100 text-blue-800",
  "Lead Contacted": "bg-yellow-100 text-yellow-800",
  "Contacted but Undecided": "bg-amber-100 text-amber-800",
  "Appointment Booked": "bg-purple-100 text-purple-800",
  "Documents Submitted": "bg-orange-100 text-orange-800",
  "Approved": "bg-cyan-100 text-cyan-800",
  "Deal Closed / Delivered": "bg-green-100 text-green-800",
  "Cancelled / Loan Denied": "bg-red-100 text-red-800"
};

// Lead classification (HCO / MCO / LCO)
const CLASSIFICATION_LABELS = {
  HCO: "High Conversion Opportunity",
  MCO: "Mid Conversion Opportunity",
  LCO: "Low Conversion Opportunity"
};

const CLASSIFICATION_SHORT = {
  HCO: "High Priority",
  MCO: "Mid Priority",
  LCO: "Low Priority"
};

const CLASSIFICATION_COLORS = {
  HCO: "bg-green-100 text-green-800 border border-green-200",
  MCO: "bg-yellow-100 text-yellow-800 border border-yellow-200",
  LCO: "bg-gray-200 text-gray-700 border border-gray-300"
};

const CLASSIFICATION_DOT = {
  HCO: "bg-green-500",
  MCO: "bg-yellow-500",
  LCO: "bg-gray-400"
};

// Human-readable labels for qualification field values
const INTEREST_TYPE_LABELS = {
  test_drive: "Test Drive",
  consultation: "Consultation Call",
  pricing_info: "Pricing Information"
};

const PURCHASE_TIMELINE_LABELS = {
  immediately: "Immediately",
  within_1_month: "Within 1 month",
  "1_3_months": "1–3 months",
  just_exploring: "Just exploring"
};

const PAYMENT_METHOD_LABELS = {
  cash: "Cash",
  bank_financing: "Bank financing",
  not_sure: "Not sure yet"
};

const MONTHLY_BUDGET_LABELS = {
  under_60k: "Under J$60,000",
  "60k_70k": "J$60,000 – J$70,000",
  "70k_80k": "J$70,000 – J$80,000",
  "80k_95k": "J$80,000 – J$95,000",
  "95k_105k": "J$95,000 – J$105,000",
  "105k_125k": "J$105,000 – J$125,000",
  above_125k: "Above J$125,000",
  other: "Other / Not sure"
};

// Status colors for progress percentage (hex values)
const STATUS_HEX_COLORS = {
  "New / Not Contacted": "#3B82F6",      // Blue
  "Lead Contacted": "#EAB308",            // Yellow
  "Contacted but Undecided": "#D97706",   // Amber
  "Appointment Booked": "#A855F7",        // Purple
  "Documents Submitted": "#F97316",       // Orange
  "Approved": "#06B6D4",               // Cyan
  "Deal Closed / Delivered": "#22C55E",   // Green
  "Cancelled / Loan Denied": "#EF4444"    // Red
};

const Sidebar = ({ isOpen, onClose }) => {
  const location = useLocation();
  const { logout, user } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  // Base nav items for all staff/admin
  const baseNavItems = [
    { path: "/admin", icon: LayoutDashboard, label: "Dashboard" },
    { path: "/admin/leads", icon: FileText, label: "Leads" },
    { path: "/admin/leads", search: "?classification=HCO", icon: TrendingUp, label: "High Priority" },
    { path: "/admin/vehicles", icon: Car, label: "Vehicles" },
  ];
  
  // Admin-only nav items
  const adminOnlyItems = [
    { path: "/admin/users", icon: Users, label: "Users" },
    { path: "/admin/settings", icon: Settings, label: "Settings" },
  ];
  
  // Combine based on role - super_admin and admin get all items
  const navItems = ["admin", "super_admin"].includes(user?.role)
    ? [...baseNavItems, ...adminOnlyItems]
    : baseNavItems;

  const getRoleBadge = () => {
    if (user?.role === "super_admin") return { text: "Super Admin", color: "bg-purple-100 text-purple-800" };
    if (user?.role === "admin") return { text: "Admin", color: "bg-pink-100 text-pink-800" };
    if (user?.role === "staff") return { text: "Staff", color: "bg-blue-100 text-blue-800" };
    return { text: "User", color: "bg-gray-100 text-gray-800" };
  };

  const roleBadge = getRoleBadge();

  return (
    <>
      {/* Overlay for mobile */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden" 
          onClick={onClose}
        />
      )}
      
      <aside className={`fixed left-0 top-0 h-full w-64 bg-[#F9F7F2] border-r border-[#E5E7EB] z-50 transform transition-transform duration-300 ${isOpen ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0`}>
        <div className="flex flex-col h-full">
          <div className="p-6 border-b border-[#E5E7EB]">
            <Link to="/">
              <img src={LOGO_URL} alt="Trish Drives Honda" className="h-12" />
            </Link>
          </div>
          
          <nav className="flex-1 p-4 space-y-1">
            {navItems.map((item) => {
              const itemSearch = item.search || "";
              const isExactMatch = location.pathname === item.path && location.search === itemSearch;
              const isPathMatch = item.path !== "/admin" && !item.search && location.pathname.startsWith(item.path) && !location.search.includes("classification=");
              const isActive = isExactMatch || (isPathMatch && item.path === location.pathname);
              const linkTo = item.search ? `${item.path}${item.search}` : item.path;
              return (
                <Link
                  key={`${item.path}${itemSearch}`}
                  to={linkTo}
                  className={`sidebar-link ${isActive ? "active" : ""}`}
                  onClick={onClose}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <item.icon className="w-5 h-5" />
                  {item.label}
                </Link>
              );
            })}
          </nav>
          
          <div className="p-4 border-t border-[#E5E7EB]">
            <div className="flex items-center gap-3 mb-2 px-4">
              <div className="w-10 h-10 rounded-full bg-[#8D7B68] flex items-center justify-center text-white font-medium">
                {user?.name?.charAt(0) || "A"}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-[#2C2420] truncate">{user?.name}</p>
                <p className="text-xs text-[#6B5E55] truncate">{user?.email}</p>
              </div>
            </div>
            <div className="px-4 mb-4">
              <span className={`text-xs px-2 py-1 rounded-full ${roleBadge.color}`}>
                {roleBadge.text}
              </span>
            </div>
            <button
              onClick={handleLogout}
              className="sidebar-link w-full text-red-600 hover:bg-red-50 hover:text-red-700"
              data-testid="logout-btn"
            >
              <LogOut className="w-5 h-5" />
              Logout
            </button>
          </div>
        </div>
      </aside>
    </>
  );
};

const Header = ({ onMenuClick }) => {
  const { user } = useAuth();
  
  return (
    <header className="sticky top-0 z-40 bg-white border-b border-[#E5E7EB] h-16 flex items-center px-6">
      <button 
        onClick={onMenuClick} 
        className="lg:hidden mr-4 p-2 hover:bg-[#F2EFE9] rounded-lg"
        data-testid="menu-toggle"
      >
        <Menu className="w-5 h-5" />
      </button>
      
      <div className="flex-1">
        <h1 className="font-heading text-xl font-semibold text-[#2C2420]">Admin Dashboard</h1>
      </div>
      
      <div className="flex items-center gap-4">
        <button className="relative p-2 hover:bg-[#F2EFE9] rounded-lg">
          <Bell className="w-5 h-5 text-[#6B5E55]" />
        </button>
        
        <div className="hidden md:flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-[#8D7B68] flex items-center justify-center text-white text-sm font-medium">
            {user?.name?.charAt(0) || "A"}
          </div>
          <span className="text-sm font-medium text-[#2C2420]">{user?.name}</span>
        </div>
      </div>
    </header>
  );
};

// Dashboard Overview
const DashboardOverview = () => {
  const [analytics, setAnalytics] = useState(null);
  const [trafficData, setTrafficData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [trafficPeriod, setTrafficPeriod] = useState("7d");
  const [dateRange, setDateRange] = useState({ from: null, to: null });
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [displayCurrency, setDisplayCurrency] = useState("JMD");
  const [exchangeRate, setExchangeRate] = useState(157); // Default JMD to USD rate

  // Fetch exchange rate
  useEffect(() => {
    const fetchExchangeRate = async () => {
      try {
        // Using a free exchange rate API
        const response = await fetch('https://api.exchangerate-api.com/v4/latest/USD');
        const data = await response.json();
        if (data.rates && data.rates.JMD) {
          setExchangeRate(data.rates.JMD);
        }
      } catch (error) {
        console.error("Failed to fetch exchange rate, using default:", error);
      }
    };
    fetchExchangeRate();
  }, []);

  // Convert amount between currencies
  const convertCurrency = (amount, fromCurrency, toCurrency) => {
    if (!amount) return 0;
    if (fromCurrency === toCurrency) return amount;
    if (fromCurrency === "JMD" && toCurrency === "USD") {
      return amount / exchangeRate;
    }
    if (fromCurrency === "USD" && toCurrency === "JMD") {
      return amount * exchangeRate;
    }
    return amount;
  };

  // Format currency for display
  const formatSalesValue = (salesData) => {
    if (!salesData) return displayCurrency === "JMD" ? "J$0" : "$0";
    
    // salesData should contain total_jmd and total_usd
    let totalInDisplayCurrency = 0;
    
    if (salesData.total_jmd) {
      totalInDisplayCurrency += convertCurrency(salesData.total_jmd, "JMD", displayCurrency);
    }
    if (salesData.total_usd) {
      totalInDisplayCurrency += convertCurrency(salesData.total_usd, "USD", displayCurrency);
    }
    
    // Fallback to old total_sales field
    if (!salesData.total_jmd && !salesData.total_usd && salesData.total) {
      totalInDisplayCurrency = salesData.total;
    }
    
    const prefix = displayCurrency === "JMD" ? "J$" : "$";
    return `${prefix}${Math.round(totalInDisplayCurrency).toLocaleString()}`;
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem("token");
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        
        // Build URL with custom date range if selected
        let trafficUrl = `${API}/analytics/traffic?period=${trafficPeriod}`;
        if (trafficPeriod === "custom" && dateRange.from && dateRange.to) {
          const startDate = format(dateRange.from, "yyyy-MM-dd");
          const endDate = format(dateRange.to, "yyyy-MM-dd");
          trafficUrl = `${API}/analytics/traffic?period=custom&start_date=${startDate}&end_date=${endDate}`;
        }
        
        const [analyticsRes, trafficRes] = await Promise.all([
          axios.get(`${API}/analytics`, { withCredentials: true, headers }),
          axios.get(trafficUrl, { withCredentials: true, headers })
        ]);
        
        setAnalytics(analyticsRes.data);
        setTrafficData(trafficRes.data);
      } catch (error) {
        console.error("Error fetching analytics:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [trafficPeriod, dateRange]);

  const handlePeriodChange = (value) => {
    if (value === "custom") {
      setIsDatePickerOpen(true);
    } else {
      setTrafficPeriod(value);
      setDateRange({ from: null, to: null });
    }
  };

  const handleDateRangeSelect = (range) => {
    setDateRange(range || { from: null, to: null });
    if (range?.from && range?.to) {
      setTrafficPeriod("custom");
      setIsDatePickerOpen(false);
    }
  };

  const getPeriodLabel = () => {
    if (trafficPeriod === "custom" && dateRange.from && dateRange.to) {
      return `${format(dateRange.from, "MMM d, yyyy")} - ${format(dateRange.to, "MMM d, yyyy")}`;
    }
    if (trafficPeriod === "all") return "All Time";
    if (trafficPeriod === "7d") return "Last 7 days";
    if (trafficPeriod === "30d") return "Last 30 days";
    if (trafficPeriod === "90d") return "Last 90 days";
    return "Select period";
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-JM', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    }).format(value || 0);
  };

  const formatNumber = (num) => {
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num?.toString() || '0';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-12 h-12 spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="dashboard-overview">
      <div>
        <h2 className="font-heading text-2xl font-bold text-[#2C2420] mb-6">Overview</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="stat-card">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 rounded-xl bg-[#8D7B68]/10 flex items-center justify-center">
                <FileText className="w-6 h-6 text-[#8D7B68]" />
              </div>
              <TrendingUp className="w-5 h-5 text-green-500" />
            </div>
            <p className="stat-value">{analytics?.total_leads || 0}</p>
            <p className="stat-label">Total Leads</p>
          </div>
          
          <div className="stat-card">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-green-600" />
              </div>
            </div>
            <p className="stat-value">{analytics?.closed_deals || 0}</p>
            <p className="stat-label">Closed Deals</p>
          </div>
          
          <div className="stat-card md:col-span-2">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 rounded-xl bg-[#D4A373]/20 flex items-center justify-center">
                <span className="text-lg font-bold text-[#8D7B68]">$</span>
              </div>
              <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
                <button
                  onClick={() => setDisplayCurrency("JMD")}
                  className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                    displayCurrency === "JMD" 
                      ? "bg-white text-[#E91E8C] shadow-sm" 
                      : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  JMD
                </button>
                <button
                  onClick={() => setDisplayCurrency("USD")}
                  className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                    displayCurrency === "USD" 
                      ? "bg-white text-[#E91E8C] shadow-sm" 
                      : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  USD
                </button>
              </div>
            </div>
            <p className="stat-value">{formatSalesValue(analytics?.sales_by_currency)}</p>
            <p className="stat-label">Total Sales Value</p>
            <p className="text-xs text-gray-400 mt-1">Rate: 1 USD = J${exchangeRate.toFixed(2)}</p>
          </div>
        </div>
      </div>

      {/* Lead Classification Widgets */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading text-2xl font-bold text-[#2C2420]">Lead Priority</h2>
          <Link 
            to="/admin/leads?classification=HCO" 
            className="text-sm text-[#E91E8C] hover:underline font-medium"
            data-testid="view-hco-link"
          >
            View High Priority →
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Link 
            to="/admin/leads?classification=HCO"
            className="stat-card hover:shadow-lg transition-shadow cursor-pointer border-l-4 border-green-500"
            data-testid="stat-hco"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-green-600" />
              </div>
              <Badge className={CLASSIFICATION_COLORS.HCO}>HCO</Badge>
            </div>
            <p className="stat-value text-green-700">{analytics?.leads_by_classification?.HCO || 0}</p>
            <p className="stat-label">High Conversion</p>
            <p className="text-xs text-[#6B5E55] mt-1">Ready to buy soon</p>
          </Link>

          <Link 
            to="/admin/leads?classification=MCO"
            className="stat-card hover:shadow-lg transition-shadow cursor-pointer border-l-4 border-yellow-500"
            data-testid="stat-mco"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 rounded-xl bg-yellow-100 flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-yellow-600" />
              </div>
              <Badge className={CLASSIFICATION_COLORS.MCO}>MCO</Badge>
            </div>
            <p className="stat-value text-yellow-700">{analytics?.leads_by_classification?.MCO || 0}</p>
            <p className="stat-label">Mid Conversion</p>
            <p className="text-xs text-[#6B5E55] mt-1">Needs nurturing</p>
          </Link>

          <Link 
            to="/admin/leads?classification=LCO"
            className="stat-card hover:shadow-lg transition-shadow cursor-pointer border-l-4 border-gray-400"
            data-testid="stat-lco"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 rounded-xl bg-gray-100 flex items-center justify-center">
                <FileText className="w-6 h-6 text-gray-600" />
              </div>
              <Badge className={CLASSIFICATION_COLORS.LCO}>LCO</Badge>
            </div>
            <p className="stat-value text-gray-700">{analytics?.leads_by_classification?.LCO || 0}</p>
            <p className="stat-label">Low Conversion</p>
            <p className="text-xs text-[#6B5E55] mt-1">Long-term or exploring</p>
          </Link>
        </div>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="dashboard-card">
          <h3 className="font-heading text-lg font-semibold text-[#2C2420] mb-4">Leads by Status</h3>
          <div className="space-y-3">
            {LEAD_STATUSES.map((status) => (
              <div key={status} className="flex items-center justify-between">
                <span className="text-sm text-[#6B5E55]">{status}</span>
                <Badge className={STATUS_COLORS[status]}>
                  {analytics?.leads_by_status?.[status] || 0}
                </Badge>
              </div>
            ))}
          </div>
        </div>
        
        <div className="dashboard-card">
          <h3 className="font-heading text-lg font-semibold text-[#2C2420] mb-4">Leads by Source</h3>
          {Object.keys(analytics?.leads_by_source || {}).length > 0 ? (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={Object.entries(analytics?.leads_by_source || {}).map(([name, value]) => ({ name, value }))}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={80}
                    paddingAngle={2}
                    dataKey="value"
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    labelLine={false}
                  >
                    {Object.entries(analytics?.leads_by_source || {}).map((_, index) => (
                      <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip 
                    formatter={(value, name) => [value, name]}
                    contentStyle={{ backgroundColor: '#fff', border: '1px solid #E5E7EB', borderRadius: '8px' }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <p className="text-sm text-gray-500 text-center py-8">No lead source data available</p>
          )}
          {/* Legend */}
          <div className="mt-4 flex flex-wrap gap-2 justify-center">
            {Object.entries(analytics?.leads_by_source || {}).map(([source, count], index) => (
              <div key={source} className="flex items-center gap-1.5 text-xs">
                <div 
                  className="w-3 h-3 rounded-full" 
                  style={{ backgroundColor: PIE_COLORS[index % PIE_COLORS.length] }}
                />
                <span className="text-[#6B5E55]">{source}: {count}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Web Traffic Analytics Section */}
      <div>
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-heading text-2xl font-bold text-[#2C2420]">Web Traffic</h2>
          <div className="flex items-center gap-3">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="flex items-center gap-2"
                  data-testid="download-report-btn"
                >
                  <Download className="w-4 h-4" />
                  Download Report
                  <ChevronDown className="w-3 h-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => downloadPDFReport(trafficData, analytics, getPeriodLabel())}>
                  <FileText className="w-4 h-4 mr-2" />
                  Download as PDF
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => downloadFullReport(trafficData, analytics, getPeriodLabel())}>
                  <Download className="w-4 h-4 mr-2" />
                  Download as Image (PNG)
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => downloadAnalyticsReport(trafficData, analytics, getPeriodLabel())}>
                  <FileText className="w-4 h-4 mr-2" />
                  Download as Text (TXT)
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            
            {/* Date Range Selector */}
            <Popover open={isDatePickerOpen} onOpenChange={setIsDatePickerOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className="w-auto min-w-[180px] justify-start text-left font-normal"
                  data-testid="date-range-btn"
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {getPeriodLabel()}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="end">
                <div className="p-3 border-b">
                  <div className="flex flex-wrap gap-2">
                    <Button
                      variant={trafficPeriod === "7d" ? "default" : "outline"}
                      size="sm"
                      onClick={() => { handlePeriodChange("7d"); setIsDatePickerOpen(false); }}
                    >
                      Last 7 days
                    </Button>
                    <Button
                      variant={trafficPeriod === "30d" ? "default" : "outline"}
                      size="sm"
                      onClick={() => { handlePeriodChange("30d"); setIsDatePickerOpen(false); }}
                    >
                      Last 30 days
                    </Button>
                    <Button
                      variant={trafficPeriod === "90d" ? "default" : "outline"}
                      size="sm"
                      onClick={() => { handlePeriodChange("90d"); setIsDatePickerOpen(false); }}
                    >
                      Last 90 days
                    </Button>
                    <Button
                      variant={trafficPeriod === "all" ? "default" : "outline"}
                      size="sm"
                      onClick={() => { handlePeriodChange("all"); setIsDatePickerOpen(false); }}
                    >
                      All Time
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">Or select a custom range below:</p>
                </div>
                <Calendar
                  mode="range"
                  selected={dateRange}
                  onSelect={handleDateRangeSelect}
                  numberOfMonths={2}
                  disabled={(date) => date > new Date()}
                />
                {dateRange.from && dateRange.to && (
                  <div className="p-3 border-t text-sm text-center text-muted-foreground">
                    {format(dateRange.from, "MMM d, yyyy")} - {format(dateRange.to, "MMM d, yyyy")}
                  </div>
                )}
              </PopoverContent>
            </Popover>
          </div>
        </div>

        {/* Traffic Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-xl border border-[#E5E7EB] p-4">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <Eye className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#2C2420]">{formatNumber(trafficData?.today_views)}</p>
                <p className="text-xs text-[#6B5E55]">Today's Views</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl border border-[#E5E7EB] p-4">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                <MousePointer className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#2C2420]">{formatNumber(trafficData?.today_visitors)}</p>
                <p className="text-xs text-[#6B5E55]">Today's Visitors</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl border border-[#E5E7EB] p-4">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                <Eye className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#2C2420]">{formatNumber(trafficData?.total_views)}</p>
                <p className="text-xs text-[#6B5E55]">Total Views</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl border border-[#E5E7EB] p-4">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-lg bg-pink-100 flex items-center justify-center">
                <Users className="w-5 h-5 text-pink-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#2C2420]">{formatNumber(trafficData?.unique_visitors)}</p>
                <p className="text-xs text-[#6B5E55]">Unique Visitors</p>
              </div>
            </div>
          </div>
        </div>

        {/* Traffic Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Views Over Time */}
          <div className="dashboard-card" id="page-views-chart">
            <h3 className="font-heading text-lg font-semibold text-[#2C2420] mb-4">Page Views Over Time</h3>
            {trafficData?.views_by_day?.length > 0 ? (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={trafficData.views_by_day}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                    <XAxis 
                      dataKey="date" 
                      tick={{ fontSize: 12, fill: '#6B5E55' }}
                      tickFormatter={(date) => new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    />
                    <YAxis tick={{ fontSize: 12, fill: '#6B5E55' }} />
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#fff', border: '1px solid #E5E7EB', borderRadius: '8px' }}
                      labelFormatter={(date) => new Date(date).toLocaleDateString()}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="views" 
                      stroke="#E91E8C" 
                      strokeWidth={2}
                      dot={{ fill: '#E91E8C', strokeWidth: 2 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <p className="text-sm text-gray-500 text-center py-12">No traffic data yet. Views will appear here as visitors browse your site.</p>
            )}
          </div>

          {/* Traffic Sources */}
          <div className="dashboard-card" id="traffic-sources-chart">
            <h3 className="font-heading text-lg font-semibold text-[#2C2420] mb-4">Traffic Sources</h3>
            {(() => {
              // Filter out development/preview environment sources
              const filteredSources = Object.entries(trafficData?.traffic_sources || {})
                .filter(([source]) => !['app.emergent.sh', '', null].includes(source?.toLowerCase()));
              return filteredSources.length > 0 ? (
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={filteredSources.map(([name, value]) => ({ 
                          name: name.charAt(0).toUpperCase() + name.slice(1), 
                          value 
                        }))}
                        cx="50%"
                        cy="50%"
                        innerRadius={50}
                        outerRadius={80}
                        paddingAngle={2}
                        dataKey="value"
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                        labelLine={false}
                      >
                        {filteredSources.map((_, index) => (
                          <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <p className="text-sm text-gray-500 text-center py-12">No traffic source data yet.</p>
              );
            })()}
          </div>
        </div>

        {/* Device Breakdown & Top Pages */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Device Breakdown */}
          <div className="dashboard-card">
            <h3 className="font-heading text-lg font-semibold text-[#2C2420] mb-4">Device Breakdown</h3>
            <div className="space-y-4">
              {Object.entries(trafficData?.devices || {}).map(([device, count]) => {
                const total = Object.values(trafficData?.devices || {}).reduce((a, b) => a + b, 0);
                const percentage = total > 0 ? Math.round((count / total) * 100) : 0;
                const Icon = device === 'mobile' ? Smartphone : Monitor;
                return (
                  <div key={device} className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center">
                      <Icon className="w-5 h-5 text-gray-600" />
                    </div>
                    <div className="flex-1">
                      <div className="flex justify-between mb-1">
                        <span className="text-sm font-medium text-[#2C2420] capitalize">{device}</span>
                        <span className="text-sm text-[#6B5E55]">{percentage}%</span>
                      </div>
                      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-[#E91E8C] rounded-full transition-all"
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
              {Object.keys(trafficData?.devices || {}).length === 0 && (
                <p className="text-sm text-gray-500 text-center py-8">No device data yet.</p>
              )}
            </div>
          </div>

          {/* Top Pages */}
          <div className="dashboard-card">
            <h3 className="font-heading text-lg font-semibold text-[#2C2420] mb-4">Top Pages</h3>
            <div className="space-y-3">
              {trafficData?.top_pages?.map((page, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 bg-[#F9F7F2] rounded-lg">
                  <div className="flex items-center gap-3">
                    <span className="w-6 h-6 rounded-full bg-[#E91E8C] text-white text-xs flex items-center justify-center font-medium">
                      {idx + 1}
                    </span>
                    <span className="text-sm text-[#2C2420] truncate max-w-[200px]">
                      {page.page === '/' ? 'Home' : page.page}
                    </span>
                  </div>
                  <span className="text-sm font-medium text-[#E91E8C]">{page.views} views</span>
                </div>
              ))}
              {(!trafficData?.top_pages || trafficData.top_pages.length === 0) && (
                <p className="text-sm text-gray-500 text-center py-8">No page data yet.</p>
              )}
            </div>
          </div>
        </div>

        {/* Visitor Locations */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Top Countries */}
          <div className="dashboard-card">
            <div className="flex items-center gap-2 mb-4">
              <Globe className="w-5 h-5 text-[#E91E8C]" />
              <h3 className="font-heading text-lg font-semibold text-[#2C2420]">Top Countries</h3>
            </div>
            <div className="space-y-3">
              {trafficData?.top_countries?.length > 0 ? (
                trafficData.top_countries.map((item, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-[#F9F7F2] rounded-lg">
                    <div className="flex items-center gap-3">
                      <span className="w-6 h-6 rounded-full bg-[#8D7B68] text-white text-xs flex items-center justify-center font-medium">
                        {idx + 1}
                      </span>
                      <span className="text-sm font-medium text-[#2C2420]">{item.country}</span>
                    </div>
                    <span className="text-sm text-[#6B5E55]">{item.count} visits</span>
                  </div>
                ))
              ) : (
                <div className="text-center py-8">
                  <Globe className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                  <p className="text-sm text-gray-500">Location data will appear as new visitors browse your site.</p>
                </div>
              )}
            </div>
          </div>

          {/* Top Cities */}
          <div className="dashboard-card">
            <div className="flex items-center gap-2 mb-4">
              <Globe className="w-5 h-5 text-[#E91E8C]" />
              <h3 className="font-heading text-lg font-semibold text-[#2C2420]">Top Cities</h3>
            </div>
            <div className="space-y-3">
              {trafficData?.top_cities?.length > 0 ? (
                trafficData.top_cities.map((item, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-[#F9F7F2] rounded-lg">
                    <div className="flex items-center gap-3">
                      <span className="w-6 h-6 rounded-full bg-[#8D7B68] text-white text-xs flex items-center justify-center font-medium">
                        {idx + 1}
                      </span>
                      <div>
                        <span className="text-sm font-medium text-[#2C2420]">{item.city}</span>
                        <span className="text-xs text-[#6B5E55] ml-1">({item.country})</span>
                      </div>
                    </div>
                    <span className="text-sm text-[#6B5E55]">{item.count} visits</span>
                  </div>
                ))
              ) : (
                <div className="text-center py-8">
                  <Globe className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                  <p className="text-sm text-gray-500">City data will appear as new visitors browse your site.</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Lead Submissions Over Time */}
        <div className="dashboard-card mt-6" id="leads-chart">
          <div className="flex items-center gap-2 mb-4">
            <FileText className="w-5 h-5 text-[#E91E8C]" />
            <h3 className="font-heading text-lg font-semibold text-[#2C2420]">Lead Submissions Over Time</h3>
          </div>
          {trafficData?.leads_by_day?.length > 0 ? (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={trafficData.leads_by_day}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                  <XAxis 
                    dataKey="date" 
                    tick={{ fontSize: 12, fill: '#6B5E55' }}
                    tickFormatter={(date) => new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  />
                  <YAxis tick={{ fontSize: 12, fill: '#6B5E55' }} allowDecimals={false} />
                  <Tooltip 
                    contentStyle={{ backgroundColor: '#fff', border: '1px solid #E5E7EB', borderRadius: '8px' }}
                    labelFormatter={(date) => new Date(date).toLocaleDateString()}
                    formatter={(value) => [value, 'Leads']}
                  />
                  <Bar 
                    dataKey="leads" 
                    fill="#E91E8C" 
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="text-center py-12">
              <FileText className="w-10 h-10 text-gray-300 mx-auto mb-2" />
              <p className="text-sm text-gray-500">No lead submission data yet. Leads will appear here as customers submit booking requests.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Leads Management
const LeadsManagement = () => {
  const [leads, setLeads] = useState([]);
  const [trashedLeads, setTrashedLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLead, setSelectedLead] = useState(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [filterStatus, setFilterStatus] = useState("");
  const [filterClassification, setFilterClassification] = useState("");
  const [users, setUsers] = useState([]);
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [deleting, setDeleting] = useState(false);
  const [viewMode, setViewMode] = useState("active"); // "active" or "trash"
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const isAdmin = ["admin", "super_admin"].includes(user?.role);

  // Sync classification filter from URL query string
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const cls = params.get("classification") || "";
    setFilterClassification(cls);
  }, [location.search]);

  // Update URL when user changes the dropdown
  const handleClassificationChange = (value) => {
    const newValue = value === "all" ? "" : value;
    const params = new URLSearchParams(location.search);
    if (newValue) {
      params.set("classification", newValue);
    } else {
      params.delete("classification");
    }
    const qs = params.toString();
    navigate(`/admin/leads${qs ? `?${qs}` : ""}`, { replace: true });
  };

  const fetchLeads = async () => {
    try {
      const token = localStorage.getItem("token");
      const params = {};
      if (filterStatus && filterStatus !== "all") params.status = filterStatus;
      if (filterClassification && filterClassification !== "all") params.classification = filterClassification;
      
      const response = await axios.get(`${API}/leads`, {
        params,
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setLeads(response.data);
      setSelectedLeads([]); // Clear selection on refresh
    } catch (error) {
      console.error("Error fetching leads:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTrashedLeads = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/leads/trash`, {
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setTrashedLeads(response.data);
    } catch (error) {
      console.error("Error fetching trashed leads:", error);
    }
  };

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/users`, {
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setUsers(response.data);
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  useEffect(() => {
    fetchLeads();
    fetchUsers();
    if (isAdmin) {
      fetchTrashedLeads();
    }
  }, [filterStatus, filterClassification]);

  const handleStatusChange = async (leadId, newStatus, saleValue = null, saleCurrency = "JMD") => {
    try {
      const token = localStorage.getItem("token");
      const data = { status: newStatus };
      if (saleValue !== null) {
        data.sale_value = saleValue;
        data.sale_currency = saleCurrency;
      }
      
      await axios.patch(`${API}/leads/${leadId}`, data, {
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      toast.success("Status updated successfully");
      fetchLeads();
      setIsDetailOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update status");
    }
  };

  const handleExport = async () => {
    try {
      const token = localStorage.getItem("token");
      window.open(`${API}/leads/export/csv`, "_blank");
    } catch (error) {
      toast.error("Failed to export leads");
    }
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedLeads(leads.map(lead => lead.lead_id));
    } else {
      setSelectedLeads([]);
    }
  };

  const handleSelectLead = (leadId, checked) => {
    if (checked) {
      setSelectedLeads(prev => [...prev, leadId]);
    } else {
      setSelectedLeads(prev => prev.filter(id => id !== leadId));
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedLeads.length === 0) {
      toast.error("No leads selected");
      return;
    }

    if (!window.confirm(`Are you sure you want to move ${selectedLeads.length} lead(s) to trash?`)) {
      return;
    }

    setDeleting(true);
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/leads/bulk-delete`, 
        { lead_ids: selectedLeads },
        {
          withCredentials: true,
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        }
      );
      toast.success(`Successfully moved ${selectedLeads.length} lead(s) to trash`);
      setSelectedLeads([]);
      fetchLeads();
      fetchTrashedLeads();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete leads");
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteSingle = async (leadId) => {
    if (!window.confirm("Are you sure you want to move this lead to trash?")) {
      return;
    }

    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/leads/${leadId}`, {
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      toast.success("Lead moved to trash");
      fetchLeads();
      fetchTrashedLeads();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete lead");
    }
  };

  const handleRestoreLead = async (leadId) => {
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/leads/${leadId}/restore`, {}, {
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      toast.success("Lead restored successfully");
      fetchLeads();
      fetchTrashedLeads();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to restore lead");
    }
  };

  const handleRestoreSelected = async () => {
    if (selectedLeads.length === 0) {
      toast.error("No leads selected");
      return;
    }

    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/leads/bulk-restore`, 
        { lead_ids: selectedLeads },
        {
          withCredentials: true,
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        }
      );
      toast.success(`Successfully restored ${selectedLeads.length} lead(s)`);
      setSelectedLeads([]);
      fetchLeads();
      fetchTrashedLeads();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to restore leads");
    }
  };

  const handlePermanentDelete = async (leadId) => {
    if (!window.confirm("Are you sure you want to PERMANENTLY delete this lead? This cannot be undone.")) {
      return;
    }

    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/leads/${leadId}/permanent`, {
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      toast.success("Lead permanently deleted");
      fetchTrashedLeads();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to permanently delete lead");
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="space-y-6" data-testid="leads-management">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="font-heading text-2xl font-bold text-[#2C2420]">
            {filterClassification === "HCO" ? "High Priority Leads" : 
             filterClassification === "MCO" ? "Mid Priority Leads" :
             filterClassification === "LCO" ? "Low Priority Leads" :
             "Leads Management"}
          </h2>
          {filterClassification && (
            <p className="text-sm text-[#6B5E55] mt-1" data-testid="classification-banner">
              Showing {CLASSIFICATION_LABELS[filterClassification] || filterClassification} leads ·{" "}
              <button
                onClick={() => handleClassificationChange("all")}
                className="text-[#E91E8C] hover:underline font-medium"
                data-testid="clear-classification-filter"
              >
                Clear filter
              </button>
            </p>
          )}
        </div>
        <div className="flex gap-3 flex-wrap">
          {isAdmin && selectedLeads.length > 0 && viewMode === "active" && (
            <Button 
              onClick={handleDeleteSelected} 
              variant="destructive" 
              className="gap-2"
              disabled={deleting}
              data-testid="delete-selected-btn"
            >
              <Trash2 className="w-4 h-4" />
              {deleting ? "Moving..." : `Move to Trash (${selectedLeads.length})`}
            </Button>
          )}
          {isAdmin && selectedLeads.length > 0 && viewMode === "trash" && (
            <Button 
              onClick={handleRestoreSelected} 
              className="gap-2 bg-green-600 hover:bg-green-700"
              data-testid="restore-selected-btn"
            >
              <Check className="w-4 h-4" />
              Restore ({selectedLeads.length})
            </Button>
          )}
          {viewMode === "active" && (
            <>
              <Select value={filterClassification || "all"} onValueChange={handleClassificationChange}>
                <SelectTrigger className="w-48 form-input" data-testid="filter-classification">
                  <SelectValue placeholder="All Priorities" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Priorities</SelectItem>
                  <SelectItem value="HCO">🟢 HCO — High Priority</SelectItem>
                  <SelectItem value="MCO">🟡 MCO — Mid Priority</SelectItem>
                  <SelectItem value="LCO">⚪ LCO — Low Priority</SelectItem>
                </SelectContent>
              </Select>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-48 form-input" data-testid="filter-status">
                  <SelectValue placeholder="All Statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  {LEAD_STATUSES.map(status => (
                    <SelectItem key={status} value={status}>{status}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button onClick={handleExport} variant="outline" className="gap-2" data-testid="export-btn">
                <Download className="w-4 h-4" />
                Export
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Active/Trash Toggle */}
      {isAdmin && (
        <div className="flex gap-2 border-b border-[#E5E7EB]">
          <button
            onClick={() => { setViewMode("active"); setSelectedLeads([]); }}
            className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
              viewMode === "active" 
                ? "border-[#E91E8C] text-[#E91E8C]" 
                : "border-transparent text-[#6B5E55] hover:text-[#2C2420]"
            }`}
            data-testid="tab-active"
          >
            Active Leads ({leads.length})
          </button>
          <button
            onClick={() => { setViewMode("trash"); setSelectedLeads([]); }}
            className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors flex items-center gap-2 ${
              viewMode === "trash" 
                ? "border-[#E91E8C] text-[#E91E8C]" 
                : "border-transparent text-[#6B5E55] hover:text-[#2C2420]"
            }`}
            data-testid="tab-trash"
          >
            <Trash2 className="w-4 h-4" />
            Trash ({trashedLeads.length})
          </button>
        </div>
      )}
      
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-12 h-12 spinner"></div>
        </div>
      ) : viewMode === "active" && leads.length === 0 ? (
        <div className="text-center py-20 bg-white rounded-2xl border border-[#E5E7EB]">
          <FileText className="w-12 h-12 text-[#6B5E55] mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-[#2C2420] mb-2">No leads found</h3>
          <p className="text-[#6B5E55]">Leads will appear here when visitors submit the booking form</p>
        </div>
      ) : viewMode === "trash" && trashedLeads.length === 0 ? (
        <div className="text-center py-20 bg-white rounded-2xl border border-[#E5E7EB]">
          <Trash2 className="w-12 h-12 text-[#6B5E55] mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-[#2C2420] mb-2">Trash is empty</h3>
          <p className="text-[#6B5E55]">Deleted leads will appear here and can be restored</p>
        </div>
      ) : viewMode === "trash" ? (
        /* Trash View */
        <div className="bg-white rounded-2xl border border-[#E5E7EB] overflow-hidden">
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr className="bg-red-50">
                  <th className="w-12">
                    <input
                      type="checkbox"
                      checked={selectedLeads.length === trashedLeads.length && trashedLeads.length > 0}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedLeads(trashedLeads.map(lead => lead.lead_id));
                        } else {
                          setSelectedLeads([]);
                        }
                      }}
                      className="rounded border-gray-300"
                    />
                  </th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Vehicle</th>
                  <th>Deleted</th>
                  <th className="text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {trashedLeads.map((lead) => (
                  <tr key={lead.lead_id} className="hover:bg-red-50/50">
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedLeads.includes(lead.lead_id)}
                        onChange={(e) => handleSelectLead(lead.lead_id, e.target.checked)}
                        className="rounded border-gray-300"
                      />
                    </td>
                    <td className="font-medium text-[#2C2420]">{lead.full_name}</td>
                    <td className="text-[#6B5E55]">{lead.email}</td>
                    <td className="text-[#6B5E55]">{lead.vehicle_interested}</td>
                    <td className="text-[#6B5E55] text-sm">{lead.deleted_at ? formatDate(lead.deleted_at) : 'N/A'}</td>
                    <td className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          size="sm"
                          onClick={() => handleRestoreLead(lead.lead_id)}
                          className="bg-green-600 hover:bg-green-700 text-white"
                          data-testid={`restore-${lead.lead_id}`}
                        >
                          Restore
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => handlePermanentDelete(lead.lead_id)}
                          data-testid={`permanent-delete-${lead.lead_id}`}
                        >
                          Delete Forever
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-[#E5E7EB] overflow-hidden">
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr className="bg-[#F9F7F2]">
                  {isAdmin && (
                    <th className="w-12">
                      <input
                        type="checkbox"
                        checked={selectedLeads.length === leads.length && leads.length > 0}
                        onChange={(e) => handleSelectAll(e.target.checked)}
                        className="w-4 h-4 rounded border-gray-300 text-[#E91E8C] focus:ring-[#E91E8C]"
                        data-testid="select-all-checkbox"
                      />
                    </th>
                  )}
                  <th>Name</th>
                  <th>Contact</th>
                  <th>Vehicle</th>
                  <th>Priority</th>
                  <th>Source</th>
                  <th>Status</th>
                  <th>Progress</th>
                  <th>Date</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {leads.map((lead) => (
                  <tr key={lead.lead_id} className={`hover:bg-[#F9F7F2] ${selectedLeads.includes(lead.lead_id) ? 'bg-pink-50' : ''}`}>
                    {isAdmin && (
                      <td onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selectedLeads.includes(lead.lead_id)}
                          onChange={(e) => handleSelectLead(lead.lead_id, e.target.checked)}
                          className="w-4 h-4 rounded border-gray-300 text-[#E91E8C] focus:ring-[#E91E8C]"
                          data-testid={`select-lead-${lead.lead_id}`}
                        />
                      </td>
                    )}
                    <td className="font-medium text-[#2C2420] cursor-pointer" onClick={() => { setSelectedLead(lead); setIsDetailOpen(true); }}>{lead.full_name}</td>
                    <td className="cursor-pointer" onClick={() => { setSelectedLead(lead); setIsDetailOpen(true); }}>
                      <div className="text-sm">
                        <p>{lead.email}</p>
                        <p className="text-[#6B5E55]">{lead.phone}</p>
                      </div>
                    </td>
                    <td className="text-[#6B5E55] cursor-pointer" onClick={() => { setSelectedLead(lead); setIsDetailOpen(true); }}>{lead.vehicle_interested}</td>
                    <td className="cursor-pointer" onClick={() => { setSelectedLead(lead); setIsDetailOpen(true); }} data-testid={`priority-cell-${lead.lead_id}`}>
                      {lead.lead_classification ? (
                        <Badge className={CLASSIFICATION_COLORS[lead.lead_classification]} title={lead.classification_reason || CLASSIFICATION_LABELS[lead.lead_classification]}>
                          <span className={`inline-block w-2 h-2 rounded-full mr-1.5 ${CLASSIFICATION_DOT[lead.lead_classification]}`}></span>
                          {lead.lead_classification}
                        </Badge>
                      ) : (
                        <span className="text-xs text-gray-400">—</span>
                      )}
                    </td>
                    <td className="text-[#6B5E55] cursor-pointer" onClick={() => { setSelectedLead(lead); setIsDetailOpen(true); }}>{lead.lead_source}</td>
                    <td className="cursor-pointer" onClick={() => { setSelectedLead(lead); setIsDetailOpen(true); }}>
                      <Badge className={STATUS_COLORS[lead.status]}>
                        {lead.status}
                      </Badge>
                    </td>
                    <td className="cursor-pointer" onClick={() => { setSelectedLead(lead); setIsDetailOpen(true); }}>
                      <div className="flex flex-col items-center justify-center">
                        <span 
                          className="text-2xl font-bold"
                          style={{ color: STATUS_HEX_COLORS[lead.status] || '#E91E8C' }}
                        >
                          {lead.status_progress}%
                        </span>
                        <Progress value={lead.status_progress} className="h-1.5 w-16 mt-1" />
                      </div>
                    </td>
                    <td className="text-[#6B5E55] cursor-pointer" onClick={() => { setSelectedLead(lead); setIsDetailOpen(true); }}>{formatDate(lead.created_at)}</td>
                    <td>
                      <div className="flex items-center gap-2">
                        <button 
                          onClick={() => { setSelectedLead(lead); setIsDetailOpen(true); }}
                          className="p-1 hover:bg-gray-100 rounded"
                        >
                          <ChevronDown className="w-4 h-4 text-[#6B5E55]" />
                        </button>
                        {isAdmin && (
                          <button 
                            onClick={(e) => { e.stopPropagation(); handleDeleteSingle(lead.lead_id); }}
                            className="p-1 hover:bg-red-50 rounded text-red-600"
                            data-testid={`delete-lead-${lead.lead_id}`}
                          >
                            <X className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      
      {/* Lead Detail Modal */}
      <LeadDetailModal
        lead={selectedLead}
        isOpen={isDetailOpen}
        onClose={() => setIsDetailOpen(false)}
        onStatusChange={handleStatusChange}
        users={users}
        onRefresh={fetchLeads}
      />
    </div>
  );
};

const LeadDetailModal = ({ lead, isOpen, onClose, onStatusChange, users, onRefresh }) => {
  const [newStatus, setNewStatus] = useState("");
  const [saleValue, setSaleValue] = useState("");
  const [saleCurrency, setSaleCurrency] = useState("JMD");
  const [notes, setNotes] = useState("");
  const [linkCopied, setLinkCopied] = useState(false);
  const leadDetailRef = useRef(null);

  useEffect(() => {
    if (lead) {
      setNewStatus(lead.status);
      setSaleValue(lead.sale_value || "");
      setSaleCurrency(lead.sale_currency || "JMD");
    }
  }, [lead]);

  const handleSave = async () => {
    if (newStatus === "Deal Closed / Delivered" && !saleValue) {
      toast.error("Sale value is required when closing a deal");
      return;
    }
    
    const saleValueNum = saleValue ? parseFloat(saleValue) : null;
    await onStatusChange(lead.lead_id, newStatus, saleValueNum, saleCurrency);
  };

  const getProgressLink = () => {
    const baseUrl = window.location.origin;
    return `${baseUrl}/progress/${lead.lead_id}`;
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(getProgressLink());
      setLinkCopied(true);
      toast.success("Progress link copied!");
      setTimeout(() => setLinkCopied(false), 2000);
    } catch (err) {
      toast.error("Failed to copy link");
    }
  };

  const handleDownloadPNG = async () => {
    if (!leadDetailRef.current) return;
    
    try {
      toast.loading("Generating image...");
      const canvas = await html2canvas(leadDetailRef.current, {
        backgroundColor: '#ffffff',
        scale: 2,
        logging: false,
        useCORS: true
      });
      
      const link = document.createElement('a');
      link.download = `lead-${lead.full_name.replace(/\s+/g, '-')}-${lead.lead_id}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
      toast.dismiss();
      toast.success("Lead details downloaded!");
    } catch (err) {
      toast.dismiss();
      toast.error("Failed to download image");
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      "New / Not Contacted": "#3B82F6",      // Blue
      "Lead Contacted": "#EAB308",            // Yellow
      "Appointment Booked": "#A855F7",        // Purple
      "Documents Submitted": "#F97316",       // Orange
      "Approved": "#06B6D4",               // Cyan
      "Deal Closed / Delivered": "#22C55E",   // Green
      "Cancelled / Loan Denied": "#EF4444"    // Red
    };
    return colors[status] || "#E91E8C";
  };

  if (!lead) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-white" data-testid="lead-detail-modal">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle className="font-heading text-xl text-[#2C2420]">
              Lead Details
            </DialogTitle>
            <div className="flex items-center gap-2">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleCopyLink}
                className="gap-2"
                data-testid="copy-progress-link"
              >
                {linkCopied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                {linkCopied ? "Copied!" : "Copy Progress Link"}
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleDownloadPNG}
                className="gap-2"
                data-testid="download-lead-png"
              >
                <Download className="w-4 h-4" />
                Download PNG
              </Button>
            </div>
          </div>
        </DialogHeader>
        
        <div ref={leadDetailRef} className="space-y-6 p-4 bg-white">
          {/* Progress Link Banner */}
          <div className="bg-[#FFF5F9] border border-[#E91E8C]/20 rounded-xl p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Share2 className="w-5 h-5 text-[#E91E8C]" />
                <div>
                  <p className="text-sm font-medium text-[#2C2420]">Customer Progress Link</p>
                  <p className="text-xs text-[#6B5E55] break-all">{getProgressLink()}</p>
                </div>
              </div>
              <Button 
                size="sm" 
                onClick={handleCopyLink}
                className="bg-[#E91E8C] hover:bg-[#C4167A] text-white"
              >
                {linkCopied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-sm text-[#6B5E55]">Full Name</Label>
              <p className="font-medium text-[#2C2420]">{lead.full_name}</p>
            </div>
            <div>
              <Label className="text-sm text-[#6B5E55]">Email</Label>
              <p className="font-medium text-[#2C2420]">{lead.email}</p>
            </div>
            <div>
              <Label className="text-sm text-[#6B5E55]">Phone</Label>
              <p className="font-medium text-[#2C2420]">{lead.phone}</p>
            </div>
            <div>
              <Label className="text-sm text-[#6B5E55]">Vehicle</Label>
              <p className="font-medium text-[#2C2420]">{lead.vehicle_interested}</p>
            </div>
            <div>
              <Label className="text-sm text-[#6B5E55]">Preferred Date</Label>
              <p className="font-medium text-[#2C2420]">{lead.preferred_date} at {lead.preferred_time}</p>
            </div>
            <div>
              <Label className="text-sm text-[#6B5E55]">Lead Source</Label>
              <p className="font-medium text-[#2C2420]">{lead.lead_source}</p>
            </div>
            <div>
              <Label className="text-sm text-[#6B5E55]">Assigned To</Label>
              <p className="font-medium text-[#2C2420]">Twong</p>
            </div>
            <div>
              <Label className="text-sm text-[#6B5E55]">Created</Label>
              <p className="font-medium text-[#2C2420]">{new Date(lead.created_at).toLocaleDateString()}</p>
            </div>
          </div>
          
          {lead.notes && (
            <div>
              <Label className="text-sm text-[#6B5E55]">Notes</Label>
              <p className="text-[#2C2420]">{lead.notes}</p>
            </div>
          )}

          {/* Lead Classification & Qualification Details */}
          {(lead.lead_classification || lead.monthly_budget || lead.purchase_timeline || lead.payment_method) && (
            <div className="bg-[#FAFAFA] border border-[#E5E7EB] rounded-xl p-4 space-y-3" data-testid="qualification-section">
              <div className="flex items-center justify-between">
                <h4 className="font-medium text-[#2C2420]">Qualification & Priority</h4>
                {lead.lead_classification && (
                  <Badge className={CLASSIFICATION_COLORS[lead.lead_classification]} data-testid="modal-classification-badge">
                    <span className={`inline-block w-2 h-2 rounded-full mr-1.5 ${CLASSIFICATION_DOT[lead.lead_classification]}`}></span>
                    {lead.lead_classification} — {CLASSIFICATION_SHORT[lead.lead_classification]}
                  </Badge>
                )}
              </div>
              {lead.classification_reason && (
                <p className="text-xs text-[#6B5E55] italic">Reason: {lead.classification_reason}</p>
              )}
              <div className="grid grid-cols-2 gap-3 text-sm pt-2">
                {lead.interest_type && (
                  <div>
                    <span className="text-[#6B5E55] text-xs">Interest</span>
                    <p className="text-[#2C2420] font-medium">{INTEREST_TYPE_LABELS[lead.interest_type] || lead.interest_type}</p>
                  </div>
                )}
                {lead.model_interested && (
                  <div>
                    <span className="text-[#6B5E55] text-xs">Model</span>
                    <p className="text-[#2C2420] font-medium">{lead.model_interested === "not_sure" ? "Not sure yet" : lead.model_interested}</p>
                  </div>
                )}
                {lead.purchase_timeline && (
                  <div>
                    <span className="text-[#6B5E55] text-xs">Timeline</span>
                    <p className="text-[#2C2420] font-medium">{PURCHASE_TIMELINE_LABELS[lead.purchase_timeline] || lead.purchase_timeline}</p>
                  </div>
                )}
                {lead.payment_method && (
                  <div>
                    <span className="text-[#6B5E55] text-xs">Payment</span>
                    <p className="text-[#2C2420] font-medium">{PAYMENT_METHOD_LABELS[lead.payment_method] || lead.payment_method}</p>
                  </div>
                )}
                {lead.monthly_budget && (
                  <div>
                    <span className="text-[#6B5E55] text-xs">Monthly Budget</span>
                    <p className="text-[#2C2420] font-medium">{MONTHLY_BUDGET_LABELS[lead.monthly_budget] || lead.monthly_budget}</p>
                  </div>
                )}
                {lead.has_trade_in && (
                  <div>
                    <span className="text-[#6B5E55] text-xs">Trade-in</span>
                    <p className="text-[#2C2420] font-medium capitalize">{lead.has_trade_in}</p>
                  </div>
                )}
                {lead.matched_affordable_model && (
                  <div className="col-span-2">
                    <span className="text-[#6B5E55] text-xs">Budget Best Matches</span>
                    <p className="text-[#2C2420] font-medium">{lead.matched_affordable_model}</p>
                  </div>
                )}
              </div>
            </div>
          )}
          
          <div className="border-t border-[#E5E7EB] pt-6">
            <h4 className="font-medium text-[#2C2420] mb-4">Update Status</h4>
            
            <div className="space-y-4">
              <div>
                <Label className="form-label">Status</Label>
                <Select value={newStatus} onValueChange={setNewStatus}>
                  <SelectTrigger className="form-input" data-testid="status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {LEAD_STATUSES.map(status => (
                      <SelectItem key={status} value={status}>{status}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {newStatus === "Deal Closed / Delivered" && (
                <div className="space-y-3">
                  <div className="flex gap-3">
                    <div className="flex-1">
                      <Label className="form-label">Sale Value *</Label>
                      <Input
                        type="number"
                        placeholder="Enter sale value"
                        value={saleValue}
                        onChange={(e) => setSaleValue(e.target.value)}
                        className="form-input"
                        data-testid="sale-value-input"
                      />
                    </div>
                    <div className="w-24">
                      <Label className="form-label">Currency</Label>
                      <Select value={saleCurrency} onValueChange={setSaleCurrency}>
                        <SelectTrigger className="form-input">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="JMD">JMD</SelectItem>
                          <SelectItem value="USD">USD</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Progress bar with percentage underneath */}
              <div className="bg-[#F9F7F2] rounded-xl p-4">
                <Label className="form-label mb-2 block">Current Progress</Label>
                <div className="text-center mb-2">
                  <span className="text-3xl font-bold text-[#E91E8C]">{STATUS_PROGRESS[newStatus]}%</span>
                </div>
                <Progress value={STATUS_PROGRESS[newStatus]} className="h-4" />
                <p className="text-xs text-center text-[#6B5E55] mt-2">{newStatus}</p>
              </div>
            </div>
          </div>
          
          {/* Activity Log */}
          {lead.activity_log && lead.activity_log.length > 0 && (
            <div className="border-t border-[#E5E7EB] pt-6">
              <h4 className="font-medium text-[#2C2420] mb-4">Activity Log</h4>
              <div className="space-y-3 max-h-48 overflow-y-auto">
                {lead.activity_log.map((log, idx) => (
                  <div key={idx} className="text-sm bg-[#F9F7F2] rounded-lg p-3">
                    <p className="text-[#2C2420]">{log.action}</p>
                    <p className="text-xs text-[#6B5E55] mt-1">
                      {new Date(log.timestamp).toLocaleString()}
                      {log.user_name && ` by ${log.user_name}`}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} className="btn-primary" data-testid="save-status-btn">
            Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Vehicles Management
const VehiclesManagement = () => {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState(null);

  const fetchVehicles = async () => {
    try {
      const response = await axios.get(`${API}/vehicles`);
      setVehicles(response.data);
    } catch (error) {
      console.error("Error fetching vehicles:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVehicles();
  }, []);

  const handleDelete = async (vehicleId) => {
    if (!window.confirm("Are you sure you want to delete this vehicle?")) return;
    
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/vehicles/${vehicleId}`, {
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      toast.success("Vehicle deleted successfully");
      fetchVehicles();
    } catch (error) {
      toast.error("Failed to delete vehicle");
    }
  };

  const formatPrice = (price, currency = "JMD", status = "available") => {
    if (status === "special_order") {
      return "Special Order";
    }
    if (!price || price === 0) {
      return "Contact for Price";
    }
    
    if (currency === "JMD") {
      // Format as JMD with 'm' suffix for millions
      if (price >= 1000000) {
        const millions = price / 1000000;
        return `JMD $${millions.toFixed(3)}m`;
      }
      return `JMD $${price.toLocaleString()}`;
    }
    
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    }).format(price);
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case "available": return "bg-green-100 text-green-800";
      case "sold": return "bg-red-100 text-red-800";
      case "special_order": return "bg-purple-100 text-purple-800";
      default: return "bg-yellow-100 text-yellow-800";
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case "available": return "Available";
      case "sold": return "Sold";
      case "special_order": return "Special Order";
      case "coming_soon": return "Coming Soon";
      default: return status;
    }
  };

  return (
    <div className="space-y-6" data-testid="vehicles-management">
      <div className="flex items-center justify-between">
        <h2 className="font-heading text-2xl font-bold text-[#2C2420]">Vehicles Management</h2>
        <Button onClick={() => { setEditingVehicle(null); setIsFormOpen(true); }} className="btn-primary" data-testid="add-vehicle-btn">
          Add Vehicle
        </Button>
      </div>
      
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-12 h-12 spinner"></div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {vehicles.map((vehicle) => (
            <div key={vehicle.vehicle_id} className="bg-white rounded-2xl border border-[#E5E7EB] overflow-hidden">
              <div className="aspect-video bg-[#F9F7F2]">
                <img
                  src={vehicle.images[0]}
                  alt={vehicle.name}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-4">
                <div className="flex items-start justify-between mb-2">
                  <h3 className="font-medium text-[#2C2420]">{vehicle.name}</h3>
                  <Badge className={getStatusBadgeClass(vehicle.status)}>
                    {getStatusLabel(vehicle.status)}
                  </Badge>
                </div>
                <p className="text-lg font-bold text-[#8D7B68] mb-4">{formatPrice(vehicle.price, vehicle.currency, vehicle.status)}</p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => { setEditingVehicle(vehicle); setIsFormOpen(true); }}
                    className="flex-1"
                    data-testid={`edit-vehicle-${vehicle.vehicle_id}`}
                  >
                    Edit
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDelete(vehicle.vehicle_id)}
                    className="text-red-600 hover:bg-red-50"
                    data-testid={`delete-vehicle-${vehicle.vehicle_id}`}
                  >
                    Delete
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      
      <VehicleFormModal
        isOpen={isFormOpen}
        onClose={() => setIsFormOpen(false)}
        vehicle={editingVehicle}
        onSave={fetchVehicles}
      />
    </div>
  );
};

const VehicleFormModal = ({ isOpen, onClose, vehicle, onSave }) => {
  const [formData, setFormData] = useState({
    name: "",
    model: "",
    year: new Date().getFullYear(),
    price: "",
    currency: "JMD",
    features: "",
    images: "",
    body_type: "SUV",
    status: "available",
    show_on_home: true,
    show_on_cars: true,
    description: ""
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (vehicle) {
      setFormData({
        name: vehicle.name,
        model: vehicle.model,
        year: vehicle.year,
        price: vehicle.price,
        currency: vehicle.currency || "JMD",
        features: vehicle.features.join("\n"),
        images: vehicle.images.join("\n"),
        body_type: vehicle.body_type,
        status: vehicle.status,
        show_on_home: vehicle.show_on_home,
        show_on_cars: vehicle.show_on_cars,
        description: vehicle.description || ""
      });
    } else {
      setFormData({
        name: "",
        model: "",
        year: new Date().getFullYear(),
        price: "",
        currency: "JMD",
        features: "",
        images: "",
        body_type: "SUV",
        status: "available",
        show_on_home: true,
        show_on_cars: true,
        description: ""
      });
    }
  }, [vehicle]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const token = localStorage.getItem("token");
      const data = {
        ...formData,
        price: parseFloat(formData.price),
        year: parseInt(formData.year),
        features: formData.features.split("\n").filter(f => f.trim()),
        images: formData.images.split("\n").filter(i => i.trim())
      };
      
      if (vehicle) {
        await axios.patch(`${API}/vehicles/${vehicle.vehicle_id}`, data, {
          withCredentials: true,
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
        toast.success("Vehicle updated successfully");
      } else {
        await axios.post(`${API}/vehicles`, data, {
          withCredentials: true,
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
        toast.success("Vehicle created successfully");
      }
      
      onSave();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save vehicle");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-white" data-testid="vehicle-form-modal">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl text-[#2C2420]">
            {vehicle ? "Edit Vehicle" : "Add Vehicle"}
          </DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="form-group">
              <Label className="form-label">Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="form-input"
                placeholder="2024 Honda CR-V"
                required
                data-testid="vehicle-name-input"
              />
            </div>
            <div className="form-group">
              <Label className="form-label">Model *</Label>
              <Input
                value={formData.model}
                onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                className="form-input"
                placeholder="CR-V"
                required
                data-testid="vehicle-model-input"
              />
            </div>
            <div className="form-group">
              <Label className="form-label">Year *</Label>
              <Input
                type="number"
                value={formData.year}
                onChange={(e) => setFormData({ ...formData, year: e.target.value })}
                className="form-input"
                required
                data-testid="vehicle-year-input"
              />
            </div>
            <div className="form-group">
              <Label className="form-label">Price *</Label>
              <div className="flex gap-2">
                <Input
                  type="number"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  className="form-input flex-1"
                  placeholder="5000000"
                  required
                  data-testid="vehicle-price-input"
                />
                <Select value={formData.currency} onValueChange={(v) => setFormData({ ...formData, currency: v })}>
                  <SelectTrigger className="w-28" data-testid="vehicle-currency-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="JMD">JMD</SelectItem>
                    <SelectItem value="USD">USD</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <p className="text-xs text-[#6B5E55] mt-1">
                {formData.currency === "JMD" ? "Enter price in Jamaican Dollars (e.g., 5675000)" : "Enter price in US Dollars"}
              </p>
            </div>
            <div className="form-group">
              <Label className="form-label">Body Type</Label>
              <Select value={formData.body_type} onValueChange={(v) => setFormData({ ...formData, body_type: v })}>
                <SelectTrigger className="form-input">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="SUV">SUV</SelectItem>
                  <SelectItem value="Sedan">Sedan</SelectItem>
                  <SelectItem value="Truck">Truck</SelectItem>
                  <SelectItem value="Minivan">Minivan</SelectItem>
                  <SelectItem value="Hatchback">Hatchback</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="form-group">
              <Label className="form-label">Status</Label>
              <Select value={formData.status} onValueChange={(v) => setFormData({ ...formData, status: v })}>
                <SelectTrigger className="form-input">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="available">Available</SelectItem>
                  <SelectItem value="sold">Sold</SelectItem>
                  <SelectItem value="coming_soon">Coming Soon</SelectItem>
                  <SelectItem value="special_order">Special Order</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div className="form-group">
            <Label className="form-label">Features (one per line)</Label>
            <Textarea
              value={formData.features}
              onChange={(e) => setFormData({ ...formData, features: e.target.value })}
              className="form-textarea"
              rows={4}
              placeholder="CVT Transmission&#10;Fuel Efficient&#10;Spacious Interior"
              data-testid="vehicle-features-input"
            />
          </div>
          
          <div className="form-group">
            <Label className="form-label">Image URLs (one per line) *</Label>
            <Textarea
              value={formData.images}
              onChange={(e) => setFormData({ ...formData, images: e.target.value })}
              className="form-textarea"
              rows={3}
              placeholder="https://example.com/image1.jpg&#10;https://example.com/image2.jpg"
              required
              data-testid="vehicle-images-input"
            />
          </div>
          
          <div className="form-group">
            <Label className="form-label">Description</Label>
            <Textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="form-textarea"
              rows={2}
              data-testid="vehicle-description-input"
            />
          </div>
          
          <div className="flex gap-4">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.show_on_home}
                onChange={(e) => setFormData({ ...formData, show_on_home: e.target.checked })}
                className="custom-checkbox"
              />
              <span className="text-sm text-[#2C2420]">Show on Home</span>
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.show_on_cars}
                onChange={(e) => setFormData({ ...formData, show_on_cars: e.target.checked })}
                className="custom-checkbox"
              />
              <span className="text-sm text-[#2C2420]">Show on Cars Page</span>
            </label>
          </div>
          
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" className="btn-primary" disabled={loading} data-testid="save-vehicle-btn">
              {loading ? "Saving..." : "Save Vehicle"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Users Management
const UsersManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    role: "staff",
    phone: ""
  });
  const [saving, setSaving] = useState(false);
  const { user: currentUser } = useAuth();

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/users`, {
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setUsers(response.data);
    } catch (error) {
      console.error("Error fetching users:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const resetForm = () => {
    setFormData({
      name: "",
      email: "",
      password: "",
      role: "staff",
      phone: ""
    });
  };

  const handleCreateUser = async () => {
    if (!formData.name || !formData.email || !formData.password) {
      toast.error("Please fill in all required fields");
      return;
    }
    
    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/users`, formData, {
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      toast.success("User created successfully");
      setShowCreateDialog(false);
      resetForm();
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create user");
    } finally {
      setSaving(false);
    }
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setFormData({
      name: user.name || "",
      email: user.email || "",
      password: "", // Don't pre-fill password
      role: user.role || "user",
      phone: user.phone || ""
    });
    setShowEditDialog(true);
  };

  const handleUpdateUser = async () => {
    if (!formData.name || !formData.email) {
      toast.error("Please fill in name and email");
      return;
    }
    
    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      const updateData = {
        name: formData.name,
        email: formData.email,
        role: formData.role,
        phone: formData.phone || null
      };
      
      // Only include password if provided
      if (formData.password) {
        updateData.password = formData.password;
      }
      
      await axios.patch(`${API}/users/${editingUser.user_id}`, updateData, {
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      toast.success("User updated successfully");
      setShowEditDialog(false);
      setEditingUser(null);
      resetForm();
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update user");
    } finally {
      setSaving(false);
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      const token = localStorage.getItem("token");
      await axios.patch(`${API}/users/${userId}`, { role: newRole }, {
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      toast.success("User role updated");
      fetchUsers();
    } catch (error) {
      toast.error("Failed to update user role");
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm("Are you sure you want to delete this user? This action cannot be undone.")) return;
    
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/users/${userId}`, {
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      toast.success("User deleted successfully");
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete user");
    }
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case "super_admin": return "bg-purple-100 text-purple-800";
      case "admin": return "bg-pink-100 text-pink-800";
      case "staff": return "bg-blue-100 text-blue-800";
      default: return "bg-gray-100 text-gray-800";
    }
  };

  const getRoleDisplayName = (role) => {
    switch (role) {
      case "super_admin": return "Super Admin";
      case "admin": return "Admin";
      case "staff": return "Staff";
      default: return "User";
    }
  };

  return (
    <div className="space-y-6" data-testid="users-management">
      <div className="flex items-center justify-between">
        <h2 className="font-heading text-2xl font-bold text-[#2C2420]">Users Management</h2>
        <Button 
          onClick={() => { resetForm(); setShowCreateDialog(true); }}
          className="bg-[#E91E8C] hover:bg-[#C4167A] text-white"
          data-testid="create-user-btn"
        >
          + Add User
        </Button>
      </div>
      
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-12 h-12 spinner"></div>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-[#E5E7EB] overflow-hidden">
          <table className="data-table">
            <thead>
              <tr className="bg-[#F9F7F2]">
                <th>Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Role</th>
                <th>Joined</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.length === 0 ? (
                <tr>
                  <td colSpan="6" className="text-center py-8 text-gray-500">
                    No users found. Click "Add User" to create one.
                  </td>
                </tr>
              ) : (
                users.map((user) => (
                  <tr key={user.user_id}>
                    <td className="font-medium text-[#2C2420]">
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-medium ${user.role === 'super_admin' ? 'bg-purple-600' : 'bg-[#E91E8C]'}`}>
                          {user.name?.charAt(0)?.toUpperCase() || "U"}
                        </div>
                        <span>{user.name}</span>
                        {user.user_id === currentUser?.user_id && (
                          <Badge variant="outline" className="text-xs">You</Badge>
                        )}
                        {user.role === 'super_admin' && (
                          <Badge className="bg-purple-100 text-purple-800 text-xs">Protected</Badge>
                        )}
                      </div>
                    </td>
                    <td className="text-[#6B5E55]">{user.email}</td>
                    <td className="text-[#6B5E55]">{user.phone || "-"}</td>
                    <td>
                      {user.role === 'super_admin' ? (
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${getRoleBadgeColor(user.role)}`}>
                          {getRoleDisplayName(user.role)}
                        </span>
                      ) : (
                        <Select 
                          value={user.role || "user"} 
                          onValueChange={(value) => handleRoleChange(user.user_id, value)}
                          disabled={user.user_id === currentUser?.user_id}
                        >
                          <SelectTrigger className={`w-28 ${getRoleBadgeColor(user.role)}`}>
                            <SelectValue>{getRoleDisplayName(user.role)}</SelectValue>
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="admin">Admin</SelectItem>
                            <SelectItem value="staff">Staff</SelectItem>
                            <SelectItem value="user">User</SelectItem>
                          </SelectContent>
                        </Select>
                      )}
                    </td>
                    <td className="text-[#6B5E55]">
                      {new Date(user.created_at).toLocaleDateString()}
                    </td>
                    <td>
                      <div className="flex items-center gap-2">
                        {/* Super Admin can edit themselves, others can't edit super_admin */}
                        {(user.role !== 'super_admin' || (user.role === 'super_admin' && currentUser?.role === 'super_admin' && user.user_id === currentUser?.user_id)) && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEditUser(user)}
                            className="text-[#E91E8C] hover:bg-pink-50"
                            data-testid={`edit-user-${user.user_id}`}
                          >
                            Edit
                          </Button>
                        )}
                        {user.user_id !== currentUser?.user_id && user.role !== 'super_admin' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(user.user_id)}
                            className="text-red-600 hover:bg-red-50"
                            data-testid={`delete-user-${user.user_id}`}
                          >
                            Delete
                          </Button>
                        )}
                        {user.role === 'super_admin' && currentUser?.role !== 'super_admin' && (
                          <span className="text-xs text-gray-400 italic">Cannot modify</span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
      
      {/* Create User Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Create New User</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="create-name">Full Name *</Label>
              <Input
                id="create-name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Enter full name"
                data-testid="create-user-name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="create-email">Email *</Label>
              <Input
                id="create-email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="Enter email address"
                data-testid="create-user-email"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="create-phone">Phone</Label>
              <Input
                id="create-phone"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="Enter phone number"
                data-testid="create-user-phone"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="create-password">Password *</Label>
              <Input
                id="create-password"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                placeholder="Enter password"
                data-testid="create-user-password"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="create-role">Role *</Label>
              <Select value={formData.role} onValueChange={(value) => setFormData({ ...formData, role: value })}>
                <SelectTrigger data-testid="create-user-role">
                  <SelectValue placeholder="Select role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="staff">Staff</SelectItem>
                  <SelectItem value="user">User</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleCreateUser} 
              disabled={saving}
              className="bg-[#E91E8C] hover:bg-[#C4167A] text-white"
              data-testid="submit-create-user"
            >
              {saving ? "Creating..." : "Create User"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Edit User Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="edit-name">Full Name *</Label>
              <Input
                id="edit-name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Enter full name"
                data-testid="edit-user-name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-email">Email *</Label>
              <Input
                id="edit-email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="Enter email address"
                data-testid="edit-user-email"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-phone">Phone</Label>
              <Input
                id="edit-phone"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="Enter phone number"
                data-testid="edit-user-phone"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-password">New Password</Label>
              <Input
                id="edit-password"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                placeholder="Leave blank to keep current"
                data-testid="edit-user-password"
              />
              <p className="text-xs text-gray-500">Leave blank to keep current password</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-role">Role *</Label>
              <Select 
                value={formData.role} 
                onValueChange={(value) => setFormData({ ...formData, role: value })}
                disabled={editingUser?.user_id === currentUser?.user_id}
              >
                <SelectTrigger data-testid="edit-user-role">
                  <SelectValue placeholder="Select role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="staff">Staff</SelectItem>
                  <SelectItem value="user">User</SelectItem>
                </SelectContent>
              </Select>
              {editingUser?.user_id === currentUser?.user_id && (
                <p className="text-xs text-gray-500">You cannot change your own role</p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleUpdateUser} 
              disabled={saving}
              className="bg-[#E91E8C] hover:bg-[#C4167A] text-white"
              data-testid="submit-edit-user"
            >
              {saving ? "Saving..." : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Settings
const SettingsPage = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/settings`, {
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setSettings(response.data);
    } catch (error) {
      console.error("Error fetching settings:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      await axios.patch(`${API}/settings`, settings, {
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      toast.success("Settings saved successfully");
    } catch (error) {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-12 h-12 spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div className="flex items-center justify-between">
        <h2 className="font-heading text-2xl font-bold text-[#2C2420]">Settings</h2>
        <Button onClick={handleSave} className="btn-primary" disabled={saving} data-testid="save-settings-btn">
          {saving ? "Saving..." : "Save Settings"}
        </Button>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="dashboard-card">
          <h3 className="font-medium text-[#2C2420] mb-4">Lead Settings</h3>
          <div className="space-y-4">
            <div className="form-group">
              <Label className="form-label">Lead Routing</Label>
              <Select 
                value={settings?.lead_routing || "manual"} 
                onValueChange={(v) => setSettings({ ...settings, lead_routing: v })}
              >
                <SelectTrigger className="form-input">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="manual">Manual Assignment</SelectItem>
                  <SelectItem value="round_robin">Round Robin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="form-group">
              <Label className="form-label">Currency</Label>
              <Select 
                value={settings?.currency || "JMD"} 
                onValueChange={(v) => setSettings({ ...settings, currency: v })}
              >
                <SelectTrigger className="form-input">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="JMD">JMD (Jamaican Dollar)</SelectItem>
                  <SelectItem value="USD">USD (US Dollar)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
        
        <div className="dashboard-card">
          <h3 className="font-medium text-[#2C2420] mb-4">Notifications</h3>
          <div className="form-group">
            <Label className="form-label">Admin Email (for lead notifications)</Label>
            <Input
              value={settings?.admin_emails?.join(", ") || ""}
              onChange={(e) => setSettings({ 
                ...settings, 
                admin_emails: e.target.value.split(",").map(s => s.trim()).filter(Boolean) 
              })}
              className="form-input"
              placeholder="admin@example.com, sales@example.com"
              data-testid="admin-emails-input"
            />
            <p className="text-xs text-[#6B5E55] mt-1">Separate multiple emails with commas</p>
          </div>
        </div>
        
        <div className="dashboard-card lg:col-span-2">
          <h3 className="font-medium text-[#2C2420] mb-4">Vehicle Options</h3>
          <div className="form-group">
            <Label className="form-label">Available Vehicles (one per line)</Label>
            <Textarea
              value={settings?.vehicle_options?.join("\n") || ""}
              onChange={(e) => setSettings({ 
                ...settings, 
                vehicle_options: e.target.value.split("\n").filter(Boolean) 
              })}
              className="form-textarea"
              rows={6}
              data-testid="vehicle-options-input"
            />
          </div>
        </div>
        
        <div className="dashboard-card lg:col-span-2">
          <h3 className="font-medium text-[#2C2420] mb-4">Lead Source Options</h3>
          <div className="form-group">
            <Label className="form-label">Lead Sources (one per line)</Label>
            <Textarea
              value={settings?.lead_source_options?.join("\n") || ""}
              onChange={(e) => setSettings({ 
                ...settings, 
                lead_source_options: e.target.value.split("\n").filter(Boolean) 
              })}
              className="form-textarea"
              rows={6}
              data-testid="lead-source-options-input"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

// Main Admin Dashboard
const AdminDashboard = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  return (
    <div className="min-h-screen bg-[#F9F7F2]" data-testid="admin-dashboard">
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      
      <div className="lg:ml-64">
        <Header onMenuClick={() => setSidebarOpen(true)} />
        
        <main className="p-6">
          <Routes>
            <Route index element={<DashboardOverview />} />
            <Route path="leads" element={<LeadsManagement />} />
            <Route path="vehicles" element={<VehiclesManagement />} />
            <Route path="users" element={<UsersManagement />} />
            <Route path="settings" element={<SettingsPage />} />
          </Routes>
        </main>
      </div>
    </div>
  );
};

export default AdminDashboard;
